import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Circle, FileText, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import type { BookingRecord, InternalBookingCommitment, InternalBookingCommitmentStatus, PlanningBlock, PlanningBlockReviewStatus, PlanningBlockStatus, VersionMirrorRecord } from "../models/production";
import { blockOverlapsRange, getDateRangeDays, isWeekend } from "../services/planningBlocks";
import { cn } from "../utils";
import { PlanningBlockModal } from "./PlanningBlockModal";

type ArtistOption = {
  id: string;
  name: string;
  department?: string;
  roleTitle?: string;
  photoUrl?: string;
  fptPersonId?: string;
  fptStatus?: string;
  fptStatusRaw?: string | boolean | number | null;
  fptStatusField?: string;
  fptStatusSyncedAt?: string;
};

type ProjectOption = {
  id: string;
  name: string;
  themeAccentColor?: string;
  fptProjectId?: string;
  googleCalendarId?: string;
};

type FptBookingResolutionFilter = "all" | "unmapped" | "manual" | "automatic";
type ArtistFptStatusFilter = "all" | "active" | "inactive" | "unknown" | string;

type DrawSelection = {
  artistId: string;
  startIndex: number;
  currentIndex: number;
};

type ConflictDay = {
  date: string;
  isWeekend: boolean;
  availableCapacity: number;
  planningAllocation: number;
  fptAllocation: number;
  totalAllocation: number;
  overAllocatedBy: number;
  conflictTypes: string[];
  planningBlocks: PlanningBlock[];
  fptBookings: BookingRecord[];
  vacationBookings: BookingRecord[];
};

type PlanningDeltaField = {
  label: string;
  before: string;
  after: string;
};

type PlanningDelta = {
  block: PlanningBlock;
  booking: BookingRecord;
  projectName: string;
  artistName: string;
  changedFields: PlanningDeltaField[];
  changedArtistDays: number;
  hasCapacityWarning: boolean;
};

type ConfirmedScheduleSummaryItem = {
  name: string;
  artistDays: number;
};

type ReviewFilter = "all" | PlanningBlockReviewStatus | "warnings";

type ChecklistItem = {
  label: string;
  ok: boolean;
};

type PublishReadinessLevel = "ready" | "warning" | "blocked";
type PublishAction = "create" | "update" | "skip" | "unknown" | "blocked" | "future_setup_needed";

type CommitmentDryRunResult = {
  commitmentId: string;
  commitment?: InternalBookingCommitment;
  status: PublishReadinessLevel;
  validationErrors: string[];
  validationWarnings: string[];
  fptAction: PublishAction;
  googleCalendarAction: PublishAction;
  sourceReferences: {
    sourcePlanningBlockId?: string;
    sourceBookingId?: string;
    sourceFptBookingId?: string;
    externalBookingId?: string;
    fptProjectId?: string;
    fptPersonId?: string;
    googleCalendarId?: string;
  };
  fptPayloadPreview?: Record<string, unknown> | null;
  googleCalendarPayloadPreview?: Record<string, unknown> | null;
};

type DryRunPublishReport = {
  generatedAt: string;
  expiresAt?: string;
  dryRunToken?: string;
  intent?: string;
  targets: string[];
  summary: {
    selectedCount: number;
    readyCount: number;
    warningCount: number;
    blockedCount: number;
    createCount: number;
    updateCount: number;
    skipCount: number;
  };
  results: CommitmentDryRunResult[];
};

type PublishReport = {
  summary: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  results: Array<{
    commitmentId: string;
    action: "create" | "update" | "skipped" | "failed";
    fptBookingId?: string;
    reason?: string;
    error?: string;
  }>;
};

type RepublishReport = {
  summary: {
    total: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  results: Array<{
    commitmentId: string;
    action: "update" | "skipped" | "failed";
    fptBookingId?: string;
    reason?: string;
    error?: string;
  }>;
};

type PublishAuditRecord = {
  id: string;
  createdAt: string;
  createdBy?: string;
  target: "fpt" | "google_calendar";
  action: "dry_run" | "publish" | "reconcile" | "republish_update";
  commitmentIds: string[];
  dryRunToken?: string;
  summary?: Record<string, unknown>;
  results?: Array<Record<string, unknown>>;
  status: "success" | "partial_success" | "failed";
  errorMessage?: string;
  expiresAt?: string;
};

type ReconcileReport = {
  summary: {
    total: number;
    matched: number;
    drifted: number;
    missing: number;
    blocked: number;
    failed: number;
  };
  results: Array<{
    commitmentId: string;
    fptBookingId?: string;
    status: "matched" | "drifted" | "missing" | "blocked" | "failed";
    errors?: string[];
    warnings?: string[];
    localValues?: Record<string, unknown>;
    fptValues?: Record<string, unknown>;
    differences?: Array<{ field: string; local: unknown; fpt: unknown }>;
    rollbackGuidance?: string[];
  }>;
};

interface ArtistBlockingCalendarProps {
  canManage: boolean;
}

const DAY_COLUMN_WIDTH = 34;
const MIN_DAY_COLUMN_WIDTH = 24;
const MAX_DAY_COLUMN_WIDTH = 56;
const ARTIST_COLUMN_WIDTH = 220;
const CALENDAR_CONTEXT_DAYS = 7;
const TIMELINE_LANE_HEIGHT = 26;
const TIMELINE_LANE_GAP = 2;
const STATUS_OPTIONS: Array<"all" | PlanningBlockStatus> = ["all", "draft", "confirmed", "published", "cancelled"];
const DEFAULT_ALLOCATION = 100;
const DEFAULT_PROJECT_COLOR = "#94a3b8";

const DEPARTMENT_COLOR_MAP: Record<string, string> = {
  "Animation": "#16d9f3",
  "Asset Build": "#ff3a1d",
  "Comp": "#1fdb3b",
  "Editorial": "#17c84a",
  "FX": "#ff1616",
  "Groom": "#8d14ff",
  "Graphics": "#bfbfbf",
  "Layout": "#5311b9",
  "Lighting": "#fff200",
  "Matchmove": "#ff8a00",
  "Matte Painting": "#1d57ff",
  "Production": "#f4a7f2",
  "Supervision": "#fff200",
  "Vendor": "#ff73b5",
  "Roto": "#ff1d91",
  "Lookdev": "#14dc2d",
  "Texture": "#7da100",
};

type TimelineLaneItem = {
  id: string;
  startIndex: number;
  endIndex: number;
  type: "booking" | "block";
};

type BlockingVersionDay = {
  day: string;
  color: string;
  projectKey: string;
};

type BlockingBookingSegment = {
  id: string;
  startDate: string;
  endDate: string;
  startIndex: number;
  endIndex: number;
  projectName: string;
  projectColor: string;
  allocationPercent: number;
  versionDays: Set<string>;
};

type BlockingArtistRow = {
  artist: ArtistOption;
  department: string;
  bookings: BlockingBookingSegment[];
  outsideVersionDays: BlockingVersionDay[];
};

type BlockingDepartmentGroup = {
  department: string;
  rows: BlockingArtistRow[];
};

function assignTimelineLanes(items: TimelineLaneItem[]) {
  const laneEndIndices: number[] = [];
  const laneById = new Map<string, number>();

  const sortedItems = [...items].sort((a, b) => {
    if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
    if (a.endIndex !== b.endIndex) return a.endIndex - b.endIndex;
    if (a.type !== b.type) return a.type === "booking" ? -1 : 1;
    return a.id.localeCompare(b.id);
  });

  for (const item of sortedItems) {
    let assignedLane = -1;
    for (let laneIndex = 0; laneIndex < laneEndIndices.length; laneIndex += 1) {
      if (item.startIndex > laneEndIndices[laneIndex]) {
        assignedLane = laneIndex;
        laneEndIndices[laneIndex] = item.endIndex;
        break;
      }
    }

    if (assignedLane === -1) {
      assignedLane = laneEndIndices.length;
      laneEndIndices.push(item.endIndex);
    }

    laneById.set(item.id, assignedLane);
  }

  return {
    laneById,
    laneCount: Math.max(1, laneEndIndices.length),
  };
}

function normalizeArtistFptStatus(value?: string | null) {
  return String(value || "unknown").trim().toLowerCase() || "unknown";
}

function toIsoDay(value?: string | null) {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return toIsoDate(parsed);
}

function startOfIsoWeek(date = new Date()) {
  const cursor = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = cursor.getUTCDay() || 7;
  cursor.setUTCDate(cursor.getUTCDate() - day + 1);
  return toIsoDate(cursor);
}

function formatDayLabel(date: string) {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "2-digit" }).format(new Date(`${date}T00:00:00.000Z`));
}

function formatRangeLabel(startDate: string, endDate: string) {
  const formatter = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return `${formatter.format(new Date(`${startDate}T00:00:00.000Z`))} - ${formatter.format(new Date(`${endDate}T00:00:00.000Z`))}`;
}

function formatCompactDateRange(startDate?: string, endDate?: string) {
  if (!startDate && !endDate) return "No dates";
  if (startDate && endDate) return formatRangeLabel(startDate, endDate);
  return startDate || endDate || "No dates";
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "").trim();
  const expanded = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) {
    return { r: 148, g: 163, b: 184 };
  }
  const numeric = Number.parseInt(expanded, 16);
  return {
    r: (numeric >> 16) & 255,
    g: (numeric >> 8) & 255,
    b: numeric & 255,
  };
}

function rgba(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeDepartmentName(value?: string | null) {
  const department = String(value || "").trim();
  if (!department) return "Unassigned";
  const match = Object.keys(DEPARTMENT_COLOR_MAP).find((entry) => entry.toLowerCase() === department.toLowerCase());
  return match || department;
}

function getDepartmentColor(department?: string | null) {
  return DEPARTMENT_COLOR_MAP[normalizeDepartmentName(department)] || DEFAULT_PROJECT_COLOR;
}

function initialsForName(name?: string | null) {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "NA";
  return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("");
}

function buildVisibleMonthMarkers(days: string[]) {
  const markers: Array<{ monthKey: string; label: string; startIndex: number; span: number }> = [];
  days.forEach((day, index) => {
    const date = new Date(`${day}T00:00:00.000Z`);
    const monthKey = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    if (markers.length === 0 || markers[markers.length - 1].monthKey !== monthKey) {
      markers.push({
        monthKey,
        label: new Intl.DateTimeFormat("en-GB", { month: "long" }).format(date).toUpperCase(),
        startIndex: index,
        span: 1,
      });
      return;
    }
    markers[markers.length - 1].span += 1;
  });
  return markers;
}

function getPersonName(person: any) {
  return person.displayName || person.fullName || person.name || person.email || "Unnamed artist";
}

function getProjectName(project: any) {
  return project.setup?.projectName || project.projectName || project.name || "Untitled project";
}

function getBlockTone(block: PlanningBlock) {
  if (block.status === "cancelled") return "border-slate-300 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400";
  if (block.blockType === "leave") return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200";
  if (block.blockType === "internal") return "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200";
  if (block.blockType === "hold") return "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200";
  if (block.status === "confirmed") return "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200";
  if (block.status === "published") return "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-900/60 dark:bg-indigo-950/40 dark:text-indigo-200";
  return "border-slate-300 bg-white text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";
}

function getBlockLabel(block: PlanningBlock) {
  if (block.blockType === "project") return block.projectName || "Project block";
  return block.blockType.charAt(0).toUpperCase() + block.blockType.slice(1);
}

function bookingOverlapsRange(booking: BookingRecord, startDate: string, endDate: string) {
  if (!booking.startDate || !booking.endDate) return false;
  return booking.startDate <= endDate && booking.endDate >= startDate;
}

function bookingOverlapsDate(booking: BookingRecord, date: string) {
  if (!booking.startDate || !booking.endDate) return false;
  return booking.startDate <= date && booking.endDate >= date;
}

function commitmentOverlapsRange(commitment: InternalBookingCommitment, startDate: string, endDate: string) {
  if (!commitment.startDate || !commitment.endDate) return false;
  return commitment.startDate <= endDate && commitment.endDate >= startDate;
}

function getBookingAllocation(booking: BookingRecord) {
  return typeof booking.percentageAllocation === "number" ? booking.percentageAllocation : DEFAULT_ALLOCATION;
}

function formatCapacityDays(percentDays: number) {
  return `${Math.round((percentDays / 100) * 10) / 10} artist-days`;
}

function getUnmappedBookingPersonLabel(booking: BookingRecord) {
  return booking.personName || booking.fptPersonId || booking.fptUserId || "Unknown Flow Production Tracking user";
}

function escapeCsvValue(value: unknown) {
  const stringValue = value == null ? "" : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
}

function downloadTextFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getAuditCount(summary: Record<string, unknown> | undefined, keys: string[]) {
  if (!summary) return 0;
  for (const key of keys) {
    const value = summary[key];
    if (typeof value === "number") return value;
  }
  return 0;
}

export function ArtistBlockingCalendar({ canManage }: ArtistBlockingCalendarProps) {
  const { user } = useAuth();
  const [rangeStart, setRangeStart] = useState(() => startOfIsoWeek());
  const [rangeEnd, setRangeEnd] = useState(() => addDays(startOfIsoWeek(), 27));
  const [dayColumnWidth, setDayColumnWidth] = useState(DAY_COLUMN_WIDTH);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | PlanningBlockStatus>("all");
  const [showFptBookings, setShowFptBookings] = useState(true);
  const [showUnmappedBookings, setShowUnmappedBookings] = useState(true);
  const [fptBookingResolutionFilter, setFptBookingResolutionFilter] = useState<FptBookingResolutionFilter>("all");
  const [artistFptStatusFilter, setArtistFptStatusFilter] = useState<ArtistFptStatusFilter>("active");
  const [artists, setArtists] = useState<ArtistOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [blocks, setBlocks] = useState<PlanningBlock[]>([]);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [versions, setVersions] = useState<VersionMirrorRecord[]>([]);
  const [internalCommitments, setInternalCommitments] = useState<InternalBookingCommitment[]>([]);
  const [publishAuditRecords, setPublishAuditRecords] = useState<PublishAuditRecord[]>([]);
  const [collapsedDepartments, setCollapsedDepartments] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<PlanningBlock | null>(null);
  const [draftBlockDefaults, setDraftBlockDefaults] = useState<Partial<PlanningBlock> | null>(null);
  const [selectedConflictArtistId, setSelectedConflictArtistId] = useState<string | null>(null);
  const [linkingBooking, setLinkingBooking] = useState<BookingRecord | null>(null);
  const [linkingPersonId, setLinkingPersonId] = useState("");
  const [linkingError, setLinkingError] = useState<string | null>(null);
  const [linkingSaving, setLinkingSaving] = useState(false);
  const [showDeltaReview, setShowDeltaReview] = useState(false);
  const [showConfirmedSchedule, setShowConfirmedSchedule] = useState(false);
  const [showInternalCommitments, setShowInternalCommitments] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("all");
  const [selectedDeltaBlockIds, setSelectedDeltaBlockIds] = useState<Set<string>>(() => new Set());
  const [selectedCommitmentIds, setSelectedCommitmentIds] = useState<Set<string>>(() => new Set());
  const [selectedReconcileCommitmentIds, setSelectedReconcileCommitmentIds] = useState<Set<string>>(() => new Set());
  const [showPublishReadiness, setShowPublishReadiness] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [dryRunReport, setDryRunReport] = useState<DryRunPublishReport | null>(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishReport, setPublishReport] = useState<PublishReport | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileReport, setReconcileReport] = useState<ReconcileReport | null>(null);
  const [republishDryRunReport, setRepublishDryRunReport] = useState<DryRunPublishReport | null>(null);
  const [republishLoading, setRepublishLoading] = useState(false);
  const [republishReport, setRepublishReport] = useState<RepublishReport | null>(null);
  const [expandedPublishAuditId, setExpandedPublishAuditId] = useState<string | null>(null);
  const [bookingSyncLoading, setBookingSyncLoading] = useState(false);
  const [fptStatusSyncLoading, setFptStatusSyncLoading] = useState(false);
  const [drawSelection, setDrawSelection] = useState<DrawSelection | null>(null);
  const [conversionReport, setConversionReport] = useState<{
    createdCount: number;
    updatedCount: number;
    skippedCount: number;
    skipped: Array<{ planningBlockId: string; reason: string }>;
  } | null>(null);

  const headers = useMemo(() => ({
    "x-user-uid": user?.uid || "",
    "x-user-email": user?.email || "",
  }), [user?.email, user?.uid]);

  const calendarStart = useMemo(() => addDays(rangeStart, -CALENDAR_CONTEXT_DAYS), [rangeStart]);
  const calendarEnd = useMemo(() => addDays(rangeEnd, CALENDAR_CONTEXT_DAYS), [rangeEnd]);
  const days = useMemo(() => getDateRangeDays(calendarStart, calendarEnd), [calendarEnd, calendarStart]);
  const today = toIsoDate(new Date());

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: calendarStart,
        endDate: calendarEnd,
      });
      if (departmentFilter !== "all") params.set("department", departmentFilter);
      if (projectFilter !== "all") params.set("projectId", projectFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);

      const [peopleRes, projectsRes, blocksRes, bookingsRes, versionsRes, commitmentsRes, auditRes] = await Promise.all([
        fetch("/api/people", { headers }),
        fetch("/api/projects", { headers }),
        fetch(`/api/planning-blocks?${params.toString()}`, { headers }),
        fetch("/api/bookings", { headers }),
        fetch("/api/versions", { headers }),
        fetch("/api/internal-booking-commitments", { headers }),
        fetch("/api/internal-booking-commitments/publish-audit?limit=20", { headers }),
      ]);

      if (!peopleRes.ok) throw new Error("Failed to load artists");
      if (!projectsRes.ok) throw new Error("Failed to load projects");
      if (!blocksRes.ok) throw new Error("Failed to load planning blocks");
      if (!bookingsRes.ok) throw new Error("Failed to load imported bookings");
      if (!versionsRes.ok) throw new Error("Failed to load published versions");
      if (!commitmentsRes.ok) throw new Error("Failed to load internal booking commitments");
      if (!auditRes.ok) {
        console.warn("Publish history is unavailable", {
          status: auditRes.status,
          statusText: auditRes.statusText,
        });
      }

      const peopleData = await peopleRes.json();
      const projectData = await projectsRes.json();
      const blockData = await blocksRes.json();
      const bookingData = await bookingsRes.json();
      const versionData = await versionsRes.json();
      const commitmentData = await commitmentsRes.json();
      const auditData = auditRes.ok ? await auditRes.json() : [];

      setArtists(
        (Array.isArray(peopleData) ? peopleData : [])
          .filter((person: any) => person.id)
          .map((person: any) => ({
            id: person.id,
            name: getPersonName(person),
            department: normalizeDepartmentName(person.department || person.roleTitle || "Unassigned"),
            roleTitle: person.roleTitle || person.role || "",
            photoUrl: person.photoUrl || person.avatarUrl || person.photo || "",
            fptPersonId: person.fptPersonId ? String(person.fptPersonId) : undefined,
            fptStatus: normalizeArtistFptStatus(person.fptStatus),
            fptStatusRaw: person.fptStatusRaw,
            fptStatusField: person.fptStatusField,
            fptStatusSyncedAt: person.fptStatusSyncedAt,
          }))
          .sort((a: ArtistOption, b: ArtistOption) => a.name.localeCompare(b.name))
      );
      setProjects(
        (Array.isArray(projectData) ? projectData : [])
          .filter((project: any) => project.id)
          .map((project: any) => ({
            id: project.id,
            name: getProjectName(project),
            themeAccentColor: project.themeAccentColor || project.setup?.themeAccentColor || undefined,
            fptProjectId: project.fptProjectId ? String(project.fptProjectId) : undefined,
            googleCalendarId: project.googleCalendarId ? String(project.googleCalendarId) : undefined,
          }))
          .sort((a: ProjectOption, b: ProjectOption) => a.name.localeCompare(b.name))
      );
      setBlocks(Array.isArray(blockData) ? blockData : []);
      setBookings(Array.isArray(bookingData) ? bookingData : []);
      setVersions(Array.isArray(versionData) ? versionData : []);
      setInternalCommitments(Array.isArray(commitmentData) ? commitmentData : []);
      setPublishAuditRecords(Array.isArray(auditData) ? auditData : []);
    } catch (error: any) {
      console.error("Failed to load artist blocking data", error);
      toast.error(error?.message || "Failed to load artist blocking data");
    } finally {
      setLoading(false);
    }
  }, [calendarEnd, calendarStart, departmentFilter, headers, projectFilter, statusFilter, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const syncFptBookingBaseline = async () => {
    if (!canManage) return;

    setBookingSyncLoading(true);
    try {
      const response = await fetch("/api/bookings/sync-fpt-baseline", {
        method: "POST",
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to refresh Flow Production Tracking booking baseline");
      }
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        toast.warning(`Refreshed ${data.syncedCount || 0} Flow Production Tracking booking${data.syncedCount === 1 ? "" : "s"} with ${data.errors.length} project error${data.errors.length === 1 ? "" : "s"}.`);
      } else {
        toast.success(`Flow Production Tracking baseline refreshed: ${data.syncedCount || 0} booking${data.syncedCount === 1 ? "" : "s"} across ${data.projectCount || 0} project${data.projectCount === 1 ? "" : "s"}.`);
      }
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to refresh Flow Production Tracking booking baseline");
    } finally {
      setBookingSyncLoading(false);
    }
  };
  const syncSelectedProjectBookings = syncFptBookingBaseline;

  const syncFptPeopleStatuses = async () => {
    if (!canManage) return;

    setFptStatusSyncLoading(true);
    try {
      const response = await fetch("/api/fpt/people/sync-statuses", {
        method: "POST",
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to sync Flow Production Tracking people statuses");
      }
      toast.success(`Synced Flow Production Tracking status for ${data.syncedCount || 0} linked people.`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to sync Flow Production Tracking people statuses");
    } finally {
      setFptStatusSyncLoading(false);
    }
  };

  const departments = useMemo(() => {
    return Array.from(new Set(artists.map((artist) => artist.department || "Unassigned"))).sort((a, b) => a.localeCompare(b));
  }, [artists]);

  const fptStatusOptions = useMemo(() => {
    return Array.from(new Set(artists.filter((artist) => artist.fptPersonId).map((artist) => normalizeArtistFptStatus(artist.fptStatus)))).sort((a, b) => a.localeCompare(b));
  }, [artists]);

  const hasSyncedFptStatuses = useMemo(() => {
    return artists.some((artist) => artist.fptPersonId && artist.fptStatusSyncedAt);
  }, [artists]);

  const visibleArtists = useMemo(() => {
    return artists.filter((artist) => {
      if (!artist.fptPersonId) return false;
      if (departmentFilter !== "all" && (artist.department || "Unassigned") !== departmentFilter) return false;
      const fptStatus = normalizeArtistFptStatus(artist.fptStatus);
      if (artistFptStatusFilter === "all") return true;
      if (artistFptStatusFilter === "active" && !hasSyncedFptStatuses) return true;
      return fptStatus === artistFptStatusFilter;
    });
  }, [artistFptStatusFilter, artists, departmentFilter, hasSyncedFptStatuses]);

  const groupedArtists = useMemo(() => {
    const groups = new Map<string, ArtistOption[]>();
    for (const artist of visibleArtists) {
      const department = artist.department || "Unassigned";
      if (!groups.has(department)) groups.set(department, []);
      groups.get(department)!.push(artist);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [visibleArtists]);

  const projectNameById = useMemo(() => new Map(projects.map((project) => [project.id, project.name])), [projects]);
  const visibleBlocks = useMemo(() => {
    const visibleArtistIds = new Set(visibleArtists.map((artist) => artist.id));
    return blocks.filter((block) => visibleArtistIds.has(block.artistId) && !block.isDeleted && blockOverlapsRange(block, calendarStart, calendarEnd));
  }, [blocks, calendarEnd, calendarStart, visibleArtists]);

  const commitmentByPlanningBlockId = useMemo(() => {
    const next = new Map<string, InternalBookingCommitment>();
    for (const commitment of internalCommitments) {
      if (commitment.isDeleted) continue;
      next.set(commitment.planningBlockId || commitment.sourcePlanningBlockId, commitment);
    }
    return next;
  }, [internalCommitments]);

  const visibleBookings = useMemo(() => {
    if (!showFptBookings) return [];
    return bookings.filter((booking) => {
      if (booking.isDeleted) return false;
      if (!bookingOverlapsRange(booking, calendarStart, calendarEnd)) return false;
      if (projectFilter !== "all" && String(booking.projectId || "") !== projectFilter) return false;
      if (fptBookingResolutionFilter === "unmapped" && booking.personId) return false;
      if (fptBookingResolutionFilter === "manual" && booking.resolutionSource !== "manual") return false;
      if (fptBookingResolutionFilter === "automatic" && (!booking.personId || booking.resolutionSource === "manual")) return false;
      return true;
    });
  }, [bookings, calendarEnd, calendarStart, fptBookingResolutionFilter, projectFilter, showFptBookings]);

  const visibleMappedBookings = useMemo(() => visibleBookings.filter((booking) => Boolean(booking.personId)), [visibleBookings]);
  const visibleMonthMarkers = useMemo(() => buildVisibleMonthMarkers(days), [days]);
  const projectById = useMemo(() => new Map(projects.map((project) => [String(project.id), project])), [projects]);
  const projectByFptProjectId = useMemo(
    () =>
      new Map(
        projects
          .filter((project) => project.fptProjectId)
          .map((project) => [String(project.fptProjectId), project] as const)
      ),
    [projects]
  );
  const getBookingLabel = (booking: BookingRecord) => {
    if (booking.isVacation) return "Flow Production Tracking vacation";
    return booking.projectId ? projectNameById.get(booking.projectId) || booking.fptProjectName || "Flow Production Tracking booking" : booking.fptProjectName || "Flow Production Tracking booking";
  };

  const blockingDepartmentGroups = useMemo<BlockingDepartmentGroup[]>(() => {
    const visibleArtistIds = new Set(visibleArtists.map((artist) => artist.id));
    const rowMap = new Map<string, BlockingArtistRow>();

    for (const artist of visibleArtists) {
      rowMap.set(artist.id, {
        artist,
        department: normalizeDepartmentName(artist.department),
        bookings: [],
        outsideVersionDays: [],
      });
    }

    const versionsByArtist = new Map<string, BlockingVersionDay[]>();
    for (const version of versions) {
      if (version.isDeleted) continue;
      let matchedArtistId = "";
      if (version.personId && visibleArtistIds.has(String(version.personId))) {
        matchedArtistId = String(version.personId);
      } else if (version.fptPersonId) {
        const matchedArtist = visibleArtists.find((artist) => artist.fptPersonId && String(artist.fptPersonId) === String(version.fptPersonId));
        if (matchedArtist) matchedArtistId = matchedArtist.id;
      }
      if (!matchedArtistId) continue;
      const day = toIsoDay(version.publishDateNormalized || version.createdAtSource || version.ingestedAt);
      if (!day || day < calendarStart || day > calendarEnd) continue;
      const project =
        (version.projectId ? projectById.get(String(version.projectId)) : null) ||
        (version.fptProjectId ? projectByFptProjectId.get(String(version.fptProjectId)) : null);
      const projectKey = String(project?.id || version.projectId || version.fptProjectId || "unknown");
      const color = project?.themeAccentColor || getDepartmentColor(project?.name) || DEFAULT_PROJECT_COLOR;
      const next = versionsByArtist.get(matchedArtistId) || [];
      next.push({ day, color, projectKey });
      versionsByArtist.set(matchedArtistId, next);
    }

    for (const booking of visibleMappedBookings) {
      const artistId = String(booking.personId || "");
      const row = rowMap.get(artistId);
      if (!row || !booking.startDate || !booking.endDate) continue;
      const startIndex = days.findIndex((day) => day >= booking.startDate!);
      const endIndex = (() => {
        let lastIndex = -1;
        days.forEach((day, index) => {
          if (day <= booking.endDate!) lastIndex = index;
        });
        return lastIndex;
      })();
      if (startIndex < 0 || endIndex < 0 || endIndex < startIndex) continue;
      const project =
        (booking.projectId ? projectById.get(String(booking.projectId)) : null) ||
        (booking.fptProjectId ? projectByFptProjectId.get(String(booking.fptProjectId)) : null);
      const projectName = getBookingLabel(booking);
      const projectColor = project?.themeAccentColor || DEFAULT_PROJECT_COLOR;
      const projectKey = String(project?.id || booking.projectId || booking.fptProjectId || booking.id);
      const versionDays = new Set(
        (versionsByArtist.get(artistId) || [])
          .filter((entry) => entry.projectKey === projectKey && entry.day >= booking.startDate! && entry.day <= booking.endDate!)
          .map((entry) => entry.day)
      );
      row.bookings.push({
        id: booking.id,
        startDate: booking.startDate,
        endDate: booking.endDate,
        startIndex,
        endIndex,
        projectName,
        projectColor,
        allocationPercent: getBookingAllocation(booking),
        versionDays,
      });
    }

    for (const [artistId, versionEntries] of versionsByArtist.entries()) {
      const row = rowMap.get(artistId);
      if (!row) continue;
      const bookedDays = new Set<string>();
      row.bookings.forEach((booking) => {
        for (let index = booking.startIndex; index <= booking.endIndex; index += 1) {
          const day = days[index];
          if (day) bookedDays.add(day);
        }
      });
      const outsideMap = new Map<string, BlockingVersionDay>();
      for (const entry of versionEntries) {
        if (bookedDays.has(entry.day)) continue;
        outsideMap.set(`${entry.projectKey}:${entry.day}`, entry);
      }
      row.outsideVersionDays = Array.from(outsideMap.values()).sort((a, b) => a.day.localeCompare(b.day));
      row.bookings.sort((a, b) => a.startDate.localeCompare(b.startDate) || a.projectName.localeCompare(b.projectName));
    }

    const groups = new Map<string, BlockingArtistRow[]>();
    for (const row of rowMap.values()) {
      if (row.bookings.length === 0 && row.outsideVersionDays.length === 0) continue;
      if (!groups.has(row.department)) groups.set(row.department, []);
      groups.get(row.department)!.push(row);
    }
    return Array.from(groups.entries())
      .map(([department, rows]) => ({
        department,
        rows: rows.sort((a, b) => a.artist.name.localeCompare(b.artist.name)),
      }))
      .sort((a, b) => a.department.localeCompare(b.department));
  }, [calendarEnd, calendarStart, days, projectByFptProjectId, projectById, projects, versions, visibleArtists, visibleMappedBookings]);

  const unmappedBookings = useMemo(() => {
    return visibleBookings
      .filter((booking) => !booking.personId)
      .sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")) || String(a.endDate || "").localeCompare(String(b.endDate || "")));
  }, [visibleBookings]);

  const getBookingResolutionLabel = (booking: BookingRecord) => {
    if (!booking.personId) return "Unmapped";
    return booking.resolutionSource === "manual" ? "Linked manually" : "Auto resolved";
  };

  const getArtistNameById = (artistId?: string) => {
    if (!artistId) return "Unassigned artist";
    return artists.find((artist) => artist.id === artistId)?.name || artistId;
  };

  const getBlockProjectLabel = (block: PlanningBlock) => {
    return block.projectId ? projectNameById.get(block.projectId) || block.projectName || "Untitled project" : block.projectName || "No project";
  };

  const findSourceBooking = (block: PlanningBlock) => {
    if (!block.sourceBookingId) return null;
    return visibleMappedBookings.find((booking) => booking.id === block.sourceBookingId) || null;
  };

  const isUnchangedFptCopy = (block: PlanningBlock) => {
    if (block.source !== "fpt_booking") return false;
    const sourceBooking = findSourceBooking(block);
    if (!sourceBooking) return false;
    return (
      block.artistId === sourceBooking.personId &&
      String(block.projectId || "") === String(sourceBooking.projectId || "") &&
      block.startDate === sourceBooking.startDate &&
      block.endDate === sourceBooking.endDate &&
      block.allocationPercent === getBookingAllocation(sourceBooking) &&
      block.blockType === (sourceBooking.isVacation ? "leave" : "project")
    );
  };

  const getPlanningDeltaFields = (block: PlanningBlock, booking: BookingRecord): PlanningDeltaField[] => {
    const fields: PlanningDeltaField[] = [];
    const sourceArtistName = getArtistNameById(booking.personId) || booking.personName || getUnmappedBookingPersonLabel(booking);
    const proposedArtistName = getArtistNameById(block.artistId) || block.artistName || "Unassigned artist";
    const sourceProjectName = getBookingLabel(booking);
    const proposedProjectName = getBlockProjectLabel(block);
    const sourceDepartment = booking.department || "Unassigned";
    const proposedDepartment = block.department || "Unassigned";
    const sourceBlockType = booking.isVacation ? "leave" : "project";
    const sourceAllocation = getBookingAllocation(booking);

    if (block.startDate !== booking.startDate) fields.push({ label: "Start date", before: booking.startDate || "No start", after: block.startDate });
    if (block.endDate !== booking.endDate) fields.push({ label: "End date", before: booking.endDate || "No end", after: block.endDate });
    if (block.allocationPercent !== sourceAllocation) fields.push({ label: "Allocation", before: `${sourceAllocation}%`, after: `${block.allocationPercent}%` });
    if (String(block.projectId || "") !== String(booking.projectId || "")) fields.push({ label: "Project", before: sourceProjectName, after: proposedProjectName });
    if (block.artistId !== booking.personId) fields.push({ label: "Artist", before: sourceArtistName, after: proposedArtistName });
    if (sourceDepartment !== proposedDepartment) fields.push({ label: "Department", before: sourceDepartment, after: proposedDepartment });
    if (block.blockType !== sourceBlockType) fields.push({ label: "Block type", before: sourceBlockType, after: block.blockType });
    if (block.status !== "draft") fields.push({ label: "Planning status", before: "draft", after: block.status });

    return fields;
  };

  const getConfirmedBlockArtistDays = (block: PlanningBlock) => {
    return getDateRangeDays(block.startDate, block.endDate)
      .filter((day) => !isWeekend(day))
      .reduce((sum) => sum + block.allocationPercent, 0);
  };

  const getBlockReviewStatus = (block: PlanningBlock): PlanningBlockReviewStatus => {
    return block.reviewStatus || "needs_review";
  };

  const capacitySummary = useMemo(() => {
    const visibleArtistIds = new Set(visibleArtists.map((artist) => artist.id));
    const workingDays = days.filter((day) => !isWeekend(day));
    const totalCapacity = visibleArtists.length * workingDays.length * DEFAULT_ALLOCATION;
    let planningAllocated = 0;
    let fptAllocated = 0;
    let overAllocatedDays = 0;
    const nextWarningByArtistDate = new Map<string, number>();

    for (const artist of visibleArtists) {
      for (const day of workingDays) {
        const dayPlanningBlocks = visibleBlocks.filter((block) => block.artistId === artist.id && block.startDate <= day && block.endDate >= day && !isUnchangedFptCopy(block));
        const dayBookings = visibleMappedBookings.filter((booking) => booking.personId === artist.id && bookingOverlapsDate(booking, day));
        const planningUsed = dayPlanningBlocks
          .filter((block) => block.blockType !== "leave")
          .reduce((sum, block) => sum + block.allocationPercent, 0);
        const planningLeave = dayPlanningBlocks
          .filter((block) => block.blockType === "leave")
          .reduce((sum, block) => sum + block.allocationPercent, 0);
        const fptUsed = dayBookings
          .filter((booking) => !booking.isVacation)
          .reduce((sum, booking) => sum + getBookingAllocation(booking), 0);
        const fptLeave = dayBookings
          .filter((booking) => booking.isVacation)
          .reduce((sum, booking) => sum + getBookingAllocation(booking), 0);
        const available = Math.max(0, DEFAULT_ALLOCATION - planningLeave - fptLeave);
        const used = planningUsed + fptUsed;

        planningAllocated += planningUsed + planningLeave;
        fptAllocated += fptUsed + fptLeave;

        if (used > available) {
          overAllocatedDays += 1;
          nextWarningByArtistDate.set(`${artist.id}:${day}`, used - available);
        }
      }
    }

    for (const block of visibleBlocks) {
      if (!visibleArtistIds.has(block.artistId)) continue;
      for (const day of days) {
        if (block.startDate <= day && block.endDate >= day && isWeekend(day)) {
          nextWarningByArtistDate.set(`${block.artistId}:${day}`, nextWarningByArtistDate.get(`${block.artistId}:${day}`) || 1);
        }
      }
    }

    return {
      totalCapacity,
      planningAllocated,
      fptAllocated,
      remainingCapacity: totalCapacity - planningAllocated - fptAllocated,
      overAllocatedDays,
      warningByArtistDate: nextWarningByArtistDate,
    };
  }, [days, visibleArtists, visibleBlocks, visibleMappedBookings]);

  const warningByArtistDateCombined = capacitySummary.warningByArtistDate;
  const warningCount = warningByArtistDateCombined.size;

  const planningDeltas = useMemo(() => {
    return visibleBlocks
      .filter((block) => block.source === "fpt_booking" && Boolean(block.sourceBookingId))
      .map((block) => {
        const booking = findSourceBooking(block);
        if (!booking) return null;
        const changedFields = getPlanningDeltaFields(block, booking);
        if (changedFields.length === 0) return null;
        const changedArtistDays = getDateRangeDays(block.startDate, block.endDate)
          .filter((day) => !isWeekend(day))
          .reduce((sum) => sum + block.allocationPercent, 0);
        const hasCapacityWarning = getDateRangeDays(block.startDate, block.endDate).some((day) => warningByArtistDateCombined.has(`${block.artistId}:${day}`));

        return {
          block,
          booking,
          projectName: getBlockProjectLabel(block),
          artistName: getArtistNameById(block.artistId),
          changedFields,
          changedArtistDays,
          hasCapacityWarning,
        };
      })
      .filter((delta): delta is PlanningDelta => Boolean(delta));
  }, [visibleBlocks, visibleMappedBookings, warningByArtistDateCombined]);

  const deltaSummary = useMemo(() => ({
    count: planningDeltas.length,
    changedArtistDays: planningDeltas.reduce((sum, delta) => sum + delta.changedArtistDays, 0),
    warningCount: planningDeltas.filter((delta) => delta.hasCapacityWarning).length,
  }), [planningDeltas]);

  const deltaGroups = useMemo(() => {
    const groups = new Map<string, Map<string, PlanningDelta[]>>();
    for (const delta of planningDeltas) {
      if (!groups.has(delta.projectName)) groups.set(delta.projectName, new Map());
      const artistGroups = groups.get(delta.projectName)!;
      if (!artistGroups.has(delta.artistName)) artistGroups.set(delta.artistName, []);
      artistGroups.get(delta.artistName)!.push(delta);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([projectName, artistGroups]) => ({
        projectName,
        artistGroups: Array.from(artistGroups.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([artistName, deltas]) => ({ artistName, deltas })),
      }));
  }, [planningDeltas]);

  useEffect(() => {
    const visibleDeltaIds = new Set(planningDeltas.map((delta) => delta.block.id));
    setSelectedDeltaBlockIds((current) => {
      const next = new Set(Array.from(current).filter((id) => visibleDeltaIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [planningDeltas]);

  const selectedDeltas = useMemo(() => {
    return planningDeltas.filter((delta) => selectedDeltaBlockIds.has(delta.block.id));
  }, [planningDeltas, selectedDeltaBlockIds]);

  const toggleDeltaSelection = (blockId: string) => {
    setSelectedDeltaBlockIds((current) => {
      const next = new Set(current);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  };

  const selectAllDeltas = () => {
    setSelectedDeltaBlockIds(new Set(planningDeltas.map((delta) => delta.block.id)));
  };

  const clearDeltaSelection = () => {
    setSelectedDeltaBlockIds(new Set());
  };

  const getDeltaWarningLabels = (delta: PlanningDelta) => {
    const warnings = new Set<string>();
    const deltaDays = getDateRangeDays(delta.block.startDate, delta.block.endDate);
    if (deltaDays.some((day) => warningByArtistDateCombined.has(`${delta.block.artistId}:${day}`) && !isWeekend(day))) {
      warnings.add("over-allocation");
    }
    if (deltaDays.some((day) => isWeekend(day))) {
      warnings.add("weekend work");
    }
    if (visibleMappedBookings.some((booking) => booking.personId === delta.block.artistId && booking.isVacation && bookingOverlapsRange(booking, delta.block.startDate, delta.block.endDate))) {
      warnings.add("vacation overlap");
    }
    return Array.from(warnings);
  };

  const confirmedBlocks = useMemo(() => {
    return visibleBlocks
      .filter((block) => block.status === "confirmed" && !block.isDeleted)
      .sort((a, b) =>
        getBlockProjectLabel(a).localeCompare(getBlockProjectLabel(b)) ||
        String(a.department || "Unassigned").localeCompare(String(b.department || "Unassigned")) ||
        getArtistNameById(a.artistId).localeCompare(getArtistNameById(b.artistId)) ||
        a.startDate.localeCompare(b.startDate)
      );
  }, [visibleBlocks, projectNameById, artists]);

  const getConfirmedBlockChecklist = (block: PlanningBlock): ChecklistItem[] => {
    const blockDays = getDateRangeDays(block.startDate, block.endDate);
    const sourceBooking = block.sourceBookingId ? bookings.find((booking) => booking.id === block.sourceBookingId && !booking.isDeleted) : null;
    const overAllocationWarning = blockDays.some((day) => warningByArtistDateCombined.has(`${block.artistId}:${day}`) && !isWeekend(day));
    const vacationConflict = visibleMappedBookings.some((booking) => booking.personId === block.artistId && booking.isVacation && bookingOverlapsRange(booking, block.startDate, block.endDate));
    const fptSourceIssue = (block.source === "fpt_booking" || block.sourceBookingId || block.sourceFptBookingId) && (!block.sourceBookingId || !sourceBooking?.personId);

    return [
      { label: "Has artist", ok: Boolean(block.artistId) },
      { label: "Has project", ok: block.blockType === "project" ? Boolean(block.projectId) : true },
      { label: "Has department", ok: Boolean(block.department) },
      { label: "Has dates", ok: Boolean(block.startDate && block.endDate) },
      { label: "Has allocation", ok: typeof block.allocationPercent === "number" && block.allocationPercent > 0 },
      { label: "No over-allocation warning", ok: !overAllocationWarning },
      { label: "No vacation conflict", ok: !vacationConflict },
      { label: "Flow Production Tracking source mapped", ok: !fptSourceIssue },
    ];
  };

  const getConfirmedBlockWarnings = (block: PlanningBlock) => {
    return getConfirmedBlockChecklist(block).filter((item) => !item.ok).map((item) => item.label);
  };

  const confirmedSummary = useMemo(() => {
    const byDepartment = new Map<string, number>();
    const byProject = new Map<string, number>();
    let totalArtistDays = 0;
    let fptDerivedCount = 0;
    let readyCount = 0;
    let blockedCount = 0;
    let needsReviewCount = 0;
    let warningCount = 0;

    for (const block of confirmedBlocks) {
      const artistDays = getConfirmedBlockArtistDays(block);
      const department = block.department || "Unassigned";
      const project = getBlockProjectLabel(block);
      totalArtistDays += artistDays;
      byDepartment.set(department, (byDepartment.get(department) || 0) + artistDays);
      byProject.set(project, (byProject.get(project) || 0) + artistDays);
      if (block.source === "fpt_booking" || block.sourceBookingId || block.sourceFptBookingId) {
        fptDerivedCount += 1;
      }
      const reviewStatus = getBlockReviewStatus(block);
      if (reviewStatus === "ready") readyCount += 1;
      if (reviewStatus === "blocked") blockedCount += 1;
      if (reviewStatus === "needs_review") needsReviewCount += 1;
      if (getConfirmedBlockWarnings(block).length > 0) warningCount += 1;
    }

    const toSortedItems = (entries: Map<string, number>): ConfirmedScheduleSummaryItem[] =>
      Array.from(entries.entries())
        .map(([name, artistDays]) => ({ name, artistDays }))
        .sort((a, b) => b.artistDays - a.artistDays || a.name.localeCompare(b.name));

    return {
      blockCount: confirmedBlocks.length,
      totalArtistDays,
      fptDerivedCount,
      readyCount,
      blockedCount,
      needsReviewCount,
      warningCount,
      byDepartment: toSortedItems(byDepartment),
      byProject: toSortedItems(byProject),
    };
  }, [confirmedBlocks]);

  const filteredConfirmedBlocks = useMemo(() => {
    return confirmedBlocks.filter((block) => {
      if (reviewFilter === "all") return true;
      if (reviewFilter === "warnings") return getConfirmedBlockWarnings(block).length > 0;
      return getBlockReviewStatus(block) === reviewFilter;
    });
  }, [confirmedBlocks, reviewFilter]);

  const readyBlocks = useMemo(() => {
    return confirmedBlocks.filter((block) => getBlockReviewStatus(block) === "ready");
  }, [confirmedBlocks]);

  const readyPacketWarnings = useMemo(() => {
    const warningCounts = new Map<string, number>();
    for (const block of readyBlocks) {
      for (const warning of getConfirmedBlockWarnings(block)) {
        warningCounts.set(warning, (warningCounts.get(warning) || 0) + 1);
      }
      if (getDateRangeDays(block.startDate, block.endDate).some((day) => isWeekend(day))) {
        warningCounts.set("Weekend included", (warningCounts.get("Weekend included") || 0) + 1);
      }
    }
    return Array.from(warningCounts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [readyBlocks]);

  const readyPacketTotals = useMemo(() => ({
    blockCount: readyBlocks.length,
    totalArtistDays: readyBlocks.reduce((sum, block) => sum + getConfirmedBlockArtistDays(block), 0),
    fptDerivedCount: readyBlocks.filter((block) => block.source === "fpt_booking" || block.sourceBookingId || block.sourceFptBookingId).length,
    warningsCount: readyBlocks.reduce((sum, block) => sum + getConfirmedBlockWarnings(block).length + (getDateRangeDays(block.startDate, block.endDate).some((day) => isWeekend(day)) ? 1 : 0), 0),
  }), [readyBlocks]);

  const readyCommitmentSummary = useMemo(() => {
    const committedCount = readyBlocks.filter((block) => commitmentByPlanningBlockId.has(block.id)).length;
    return {
      eligibleCount: readyBlocks.length,
      committedCount,
      uncommittedCount: readyBlocks.length - committedCount,
      warningCount: readyBlocks.filter((block) => getConfirmedBlockWarnings(block).length > 0).length,
    };
  }, [commitmentByPlanningBlockId, readyBlocks]);

  const visibleInternalCommitments = useMemo(() => {
    return internalCommitments
      .filter((commitment) => {
        if (commitment.isDeleted) return false;
        if (!commitmentOverlapsRange(commitment, rangeStart, rangeEnd)) return false;
        if (projectFilter !== "all" && String(commitment.projectId || "") !== projectFilter) return false;
        if (departmentFilter !== "all" && String(commitment.department || "Unassigned") !== departmentFilter) return false;
        return true;
      })
      .sort((a, b) =>
        String(a.projectName || "").localeCompare(String(b.projectName || "")) ||
        String(a.department || "").localeCompare(String(b.department || "")) ||
        String(a.personName || "").localeCompare(String(b.personName || "")) ||
        a.startDate.localeCompare(b.startDate)
      );
  }, [departmentFilter, internalCommitments, projectFilter, rangeEnd, rangeStart]);

  const internalCommitmentGroups = useMemo(() => {
    const projectGroups = new Map<string, Map<string, Map<string, InternalBookingCommitment[]>>>();
    for (const commitment of visibleInternalCommitments) {
      const project = commitment.projectName || commitment.projectId || "No project";
      const department = commitment.department || "Unassigned";
      const artist = commitment.personName || commitment.personId || "Unassigned artist";
      if (!projectGroups.has(project)) projectGroups.set(project, new Map());
      const departmentGroups = projectGroups.get(project)!;
      if (!departmentGroups.has(department)) departmentGroups.set(department, new Map());
      const artistGroups = departmentGroups.get(department)!;
      if (!artistGroups.has(artist)) artistGroups.set(artist, []);
      artistGroups.get(artist)!.push(commitment);
    }
    return Array.from(projectGroups.entries()).map(([projectName, departmentGroups]) => ({
      projectName,
      departmentGroups: Array.from(departmentGroups.entries()).map(([department, artistGroups]) => ({
        department,
        artistGroups: Array.from(artistGroups.entries()).map(([artistName, commitments]) => ({ artistName, commitments })),
      })),
    }));
  }, [visibleInternalCommitments]);

  const internalCommitmentSummary = useMemo(() => ({
    count: visibleInternalCommitments.length,
    planned: visibleInternalCommitments.filter((commitment) => commitment.commitmentStatus === "planned").length,
    superseded: visibleInternalCommitments.filter((commitment) => commitment.commitmentStatus === "superseded").length,
    cancelled: visibleInternalCommitments.filter((commitment) => commitment.commitmentStatus === "cancelled").length,
  }), [visibleInternalCommitments]);

  const eligiblePublishCommitmentIds = useMemo(() => {
    return new Set(
      visibleInternalCommitments
        .filter((commitment) => commitment.commitmentStatus === "planned" && !commitment.isDeleted)
        .map((commitment) => commitment.id)
    );
  }, [visibleInternalCommitments]);

  const eligibleReconcileCommitmentIds = useMemo(() => {
    return new Set(
      visibleInternalCommitments
        .filter((commitment) => !commitment.isDeleted && Boolean(commitment.externalBookingId || commitment.sourceFptBookingId))
        .map((commitment) => commitment.id)
    );
  }, [visibleInternalCommitments]);

  useEffect(() => {
    setSelectedCommitmentIds((current) => {
      const eligibleVisible = visibleInternalCommitments
        .filter((commitment) => commitment.commitmentStatus === "planned" && !commitment.isDeleted)
        .map((commitment) => commitment.id);
      const eligibleSet = new Set(eligibleVisible);
      const retained = new Set(Array.from(current).filter((id) => eligibleSet.has(id)));
      if (showInternalCommitments && retained.size === 0 && eligibleVisible.length > 0) {
        return new Set(eligibleVisible);
      }
      return retained;
    });
  }, [showInternalCommitments, visibleInternalCommitments]);

  useEffect(() => {
    setSelectedReconcileCommitmentIds((current) => {
      const eligibleVisible = visibleInternalCommitments
        .filter((commitment) => !commitment.isDeleted && Boolean(commitment.externalBookingId || commitment.sourceFptBookingId))
        .map((commitment) => commitment.id);
      const eligibleSet = new Set(eligibleVisible);
      const retained = new Set(Array.from(current).filter((id) => eligibleSet.has(id)));
      if (showReconciliation && retained.size === 0 && eligibleVisible.length > 0) {
        return new Set(eligibleVisible);
      }
      return retained;
    });
  }, [showReconciliation, visibleInternalCommitments]);

  const dryRunResults = dryRunReport?.results || [];
  const dryRunSummary = dryRunReport?.summary || {
    selectedCount: 0,
    readyCount: 0,
    warningCount: 0,
    blockedCount: 0,
    createCount: 0,
    updateCount: 0,
    skipCount: 0,
  };

  const readySchedulePacket = useMemo(() => {
    const groupedProjects = new Map<string, Map<string, Map<string, PlanningBlock[]>>>();
    for (const block of readyBlocks) {
      const project = getBlockProjectLabel(block);
      const department = block.department || "Unassigned";
      const artist = getArtistNameById(block.artistId);
      if (!groupedProjects.has(project)) groupedProjects.set(project, new Map());
      const departmentGroups = groupedProjects.get(project)!;
      if (!departmentGroups.has(department)) departmentGroups.set(department, new Map());
      const artistGroups = departmentGroups.get(department)!;
      if (!artistGroups.has(artist)) artistGroups.set(artist, []);
      artistGroups.get(artist)!.push(block);
    }

    return {
      metadata: {
        generatedAt: new Date().toISOString(),
        generatedBy: user?.email || user?.uid || undefined,
        rangeStart,
        rangeEnd,
      },
      totals: readyPacketTotals,
      warnings: readyPacketWarnings,
      groupedBlocks: Array.from(groupedProjects.entries()).map(([projectName, departments]) => ({
        projectName,
        departments: Array.from(departments.entries()).map(([department, artists]) => ({
          department,
          artists: Array.from(artists.entries()).map(([artistName, artistBlocks]) => ({
            artistName,
            blocks: artistBlocks.map((block) => ({
              id: block.id,
              projectName: getBlockProjectLabel(block),
              projectId: block.projectId,
              department: block.department || "Unassigned",
              artistName: getArtistNameById(block.artistId),
              artistId: block.artistId,
              startDate: block.startDate,
              endDate: block.endDate,
              allocationPercent: block.allocationPercent,
              blockType: block.blockType,
              status: block.status,
              reviewStatus: getBlockReviewStatus(block),
              reviewNote: block.reviewNote,
              source: block.source,
              sourceBookingId: block.sourceBookingId,
              sourceFptBookingId: block.sourceFptBookingId,
              note: block.note,
              warnings: [
                ...getConfirmedBlockWarnings(block),
                ...(getDateRangeDays(block.startDate, block.endDate).some((day) => isWeekend(day)) ? ["Weekend included"] : []),
              ],
            })),
          })),
        })),
      })),
    };
  }, [rangeEnd, rangeStart, readyBlocks, readyPacketTotals, readyPacketWarnings, user?.email, user?.uid]);

  const confirmedScheduleGroups = useMemo(() => {
    const projectGroups = new Map<string, Map<string, Map<string, PlanningBlock[]>>>();
    for (const block of filteredConfirmedBlocks) {
      const project = getBlockProjectLabel(block);
      const department = block.department || "Unassigned";
      const artist = getArtistNameById(block.artistId);
      if (!projectGroups.has(project)) projectGroups.set(project, new Map());
      const departmentGroups = projectGroups.get(project)!;
      if (!departmentGroups.has(department)) departmentGroups.set(department, new Map());
      const artistGroups = departmentGroups.get(department)!;
      if (!artistGroups.has(artist)) artistGroups.set(artist, []);
      artistGroups.get(artist)!.push(block);
    }

    return Array.from(projectGroups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([projectName, departmentGroups]) => ({
        projectName,
        departmentGroups: Array.from(departmentGroups.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([department, artistGroups]) => ({
            department,
            artistGroups: Array.from(artistGroups.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([artistName, blocks]) => ({ artistName, blocks })),
          })),
      }));
  }, [filteredConfirmedBlocks, projectNameById, artists]);

  const getArtistBookings = (artistId: string) => {
    return visibleMappedBookings
      .filter((booking) => booking.personId === artistId)
      .sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")) || String(a.endDate || "").localeCompare(String(b.endDate || "")));
  };

  const getArtistWarnings = (artistId: string, artistBlocks: PlanningBlock[], artistBookings: BookingRecord[]) => {
    const messages: string[] = [];
    const overAllocatedDays = days.filter((day) => warningByArtistDateCombined.has(`${artistId}:${day}`) && !isWeekend(day)).length;
    if (overAllocatedDays > 0) {
      messages.push(`${overAllocatedDays} over-allocated working day${overAllocatedDays === 1 ? "" : "s"}`);
    }
    const vacationOverlap = artistBlocks.some((block) =>
      artistBookings.some((booking) => booking.isVacation && bookingOverlapsRange(booking, block.startDate, block.endDate))
    );
    if (vacationOverlap) {
      messages.push("Planning overlaps Flow Production Tracking vacation");
    }
    const weekendOverlap = artistBlocks.some((block) => days.some((day) => isWeekend(day) && block.startDate <= day && block.endDate >= day));
    if (weekendOverlap) {
      messages.push("Planning includes weekend days");
    }
    if (artistBlocks.some((block) => block.source === "fpt_booking" && findSourceBooking(block) && !isUnchangedFptCopy(block))) {
      messages.push("Copied Flow Production Tracking block differs from baseline");
    }
    return messages;
  };

  const getArtistConflictDetails = (artist: ArtistOption): ConflictDay[] => {
    return days
      .map((day) => {
        const dayPlanningBlocks = visibleBlocks.filter((block) => block.artistId === artist.id && block.startDate <= day && block.endDate >= day && !isUnchangedFptCopy(block));
        const dayBookings = visibleMappedBookings.filter((booking) => booking.personId === artist.id && bookingOverlapsDate(booking, day));
        const vacationBookings = dayBookings.filter((booking) => booking.isVacation);
        const planningLeaveAllocation = dayPlanningBlocks
          .filter((block) => block.blockType === "leave")
          .reduce((sum, block) => sum + block.allocationPercent, 0);
        const planningAllocation = dayPlanningBlocks
          .filter((block) => block.blockType !== "leave")
          .reduce((sum, block) => sum + block.allocationPercent, 0);
        const fptLeaveAllocation = vacationBookings.reduce((sum, booking) => sum + getBookingAllocation(booking), 0);
        const fptAllocation = dayBookings
          .filter((booking) => !booking.isVacation)
          .reduce((sum, booking) => sum + getBookingAllocation(booking), 0);
        const availableCapacity = Math.max(0, DEFAULT_ALLOCATION - planningLeaveAllocation - fptLeaveAllocation);
        const totalAllocation = planningAllocation + fptAllocation;
        const conflictTypes: string[] = [];

        if (totalAllocation > availableCapacity) conflictTypes.push("Over allocated");
        if (dayPlanningBlocks.some((block) => block.blockType !== "leave") && vacationBookings.length > 0) conflictTypes.push("Planning overlaps Flow Production Tracking vacation");
        if (isWeekend(day) && dayPlanningBlocks.length > 0) conflictTypes.push("Planning includes weekend");

        return {
          date: day,
          isWeekend: isWeekend(day),
          availableCapacity,
          planningAllocation,
          fptAllocation,
          totalAllocation,
          overAllocatedBy: Math.max(0, totalAllocation - availableCapacity),
          conflictTypes,
          planningBlocks: dayPlanningBlocks,
          fptBookings: dayBookings.filter((booking) => !booking.isVacation),
          vacationBookings,
        };
      })
      .filter((day) => day.conflictTypes.length > 0);
  };

  const selectedConflictArtist = useMemo(
    () => artists.find((artist) => artist.id === selectedConflictArtistId) || null,
    [artists, selectedConflictArtistId]
  );

  const selectedConflictDays = useMemo(
    () => (selectedConflictArtist ? getArtistConflictDetails(selectedConflictArtist) : []),
    [selectedConflictArtist, days, visibleBlocks, visibleMappedBookings]
  );

  const editConflictPlanningBlock = (block: PlanningBlock) => {
    setSelectedConflictArtistId(null);
    openEditModal(block);
  };

  const openLinkBookingModal = (booking: BookingRecord) => {
    setLinkingBooking(booking);
    setLinkingPersonId(booking.personId || "");
    setLinkingError(null);
  };

  const closeLinkBookingModal = () => {
    if (linkingSaving) return;
    setLinkingBooking(null);
    setLinkingPersonId("");
    setLinkingError(null);
  };

  const linkBookingToArtist = async () => {
    if (!linkingBooking || !linkingPersonId) {
      setLinkingError("Choose an artist to link this Flow Production Tracking booking.");
      return;
    }
    setLinkingSaving(true);
    setLinkingError(null);
    try {
      const response = await fetch(`/api/bookings/${encodeURIComponent(linkingBooking.id)}/link-person`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({ personId: linkingPersonId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to link Flow Production Tracking booking");
      }
      toast.success("Flow Production Tracking booking linked to artist");
      setLinkingBooking(null);
      setLinkingPersonId("");
      await loadData();
    } catch (error: any) {
      setLinkingError(error?.message || "Failed to link Flow Production Tracking booking");
    } finally {
      setLinkingSaving(false);
    }
  };

  const unlinkBookingFromArtist = async () => {
    if (!linkingBooking) return;
    setLinkingSaving(true);
    setLinkingError(null);
    try {
      const response = await fetch(`/api/bookings/${encodeURIComponent(linkingBooking.id)}/link-person`, {
        method: "DELETE",
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to undo Flow Production Tracking booking link");
      }
      toast.success("Manual Flow Production Tracking booking link removed");
      setLinkingBooking(null);
      setLinkingPersonId("");
      await loadData();
    } catch (error: any) {
      setLinkingError(error?.message || "Failed to undo Flow Production Tracking booking link");
    } finally {
      setLinkingSaving(false);
    }
  };

  const openCreateModal = () => {
    setDraftBlockDefaults(null);
    setEditingBlock(null);
    setModalOpen(true);
  };

  const beginDrawPlanningBlock = (artist: ArtistOption, dayIndex: number, event: React.MouseEvent) => {
    if (!canManage || event.button !== 0) return;
    event.preventDefault();
    setDrawSelection({ artistId: artist.id, startIndex: dayIndex, currentIndex: dayIndex });
  };

  const updateDrawPlanningBlock = (artistId: string, dayIndex: number) => {
    setDrawSelection((current) => {
      if (!current || current.artistId !== artistId) return current;
      return { ...current, currentIndex: dayIndex };
    });
  };

  const finishDrawPlanningBlock = useCallback(() => {
    setDrawSelection((current) => {
      if (!current) return null;
      const artist = artists.find((candidate) => candidate.id === current.artistId);
      const startIndex = Math.min(current.startIndex, current.currentIndex);
      const endIndex = Math.max(current.startIndex, current.currentIndex);
      const startDate = days[startIndex];
      const endDate = days[endIndex];
      if (!artist || !startDate || !endDate) return null;

      setEditingBlock(null);
      setDraftBlockDefaults({
        artistId: artist.id,
        artistName: artist.name,
        department: artist.department,
        startDate,
        endDate,
        allocationPercent: DEFAULT_ALLOCATION,
        status: "draft",
        blockType: "project",
        source: "manual",
      });
      setModalOpen(true);
      toast.info("Choose a project to finish the new planning block.");
      return null;
    });
  }, [artists, days]);

  useEffect(() => {
    if (!drawSelection) return;
    const handleMouseUp = () => finishDrawPlanningBlock();
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [drawSelection, finishDrawPlanningBlock]);

  const openEditModal = (block: PlanningBlock) => {
    if (!canManage) return;
    setDraftBlockDefaults(null);
    setEditingBlock(block);
    setModalOpen(true);
  };

  const createPlanningBlockFromBooking = (booking: BookingRecord) => {
    if (!canManage) return;
    if (!booking.personId) {
      setLinkingError("Link this Flow Production Tracking booking to an artist before creating a PlanningBlock.");
      openLinkBookingModal(booking);
      return;
    }
    setSelectedConflictArtistId(null);
    setLinkingBooking(null);
    setEditingBlock(null);
    setDraftBlockDefaults({
      artistId: booking.personId,
      artistName: booking.personName,
      projectId: booking.projectId,
      projectName: booking.projectId ? projectNameById.get(booking.projectId) || booking.fptProjectName : booking.fptProjectName,
      department: booking.department,
      startDate: booking.startDate || rangeStart,
      endDate: booking.endDate || booking.startDate || rangeStart,
      allocationPercent: getBookingAllocation(booking),
      status: "draft",
      blockType: booking.isVacation ? "leave" : "project",
      source: "fpt_booking",
      sourceBookingId: booking.id,
      sourceFptBookingId: booking.fptBookingId,
      note: booking.note ? `Copied from Flow Production Tracking booking: ${booking.note}` : "Copied from Flow Production Tracking booking",
    });
    setModalOpen(true);
  };

  const buildBaselinePayloadFromBooking = (booking: BookingRecord) => ({
    artistId: booking.personId,
    artistName: booking.personId ? getArtistNameById(booking.personId) : booking.personName,
    projectId: booking.projectId,
    projectName: booking.projectId ? projectNameById.get(booking.projectId) || booking.fptProjectName : booking.fptProjectName,
    department: booking.department,
    startDate: booking.startDate,
    endDate: booking.endDate || booking.startDate,
    allocationPercent: getBookingAllocation(booking),
    status: "draft",
    blockType: booking.isVacation ? "leave" : "project",
    note: booking.note ? `Copied from Flow Production Tracking booking: ${booking.note}` : "Copied from Flow Production Tracking booking",
    source: "fpt_booking",
    sourceBookingId: booking.id,
    sourceFptBookingId: booking.fptBookingId,
  });

  const buildPlanningBlockPayload = (block: PlanningBlock, overrides: Partial<PlanningBlock> = {}) => ({
    artistId: overrides.artistId ?? block.artistId,
    artistName: overrides.artistName ?? block.artistName,
    projectId: overrides.projectId ?? block.projectId,
    projectName: overrides.projectName ?? block.projectName,
    department: overrides.department ?? block.department,
    startDate: overrides.startDate ?? block.startDate,
    endDate: overrides.endDate ?? block.endDate,
    allocationPercent: overrides.allocationPercent ?? block.allocationPercent,
    status: overrides.status ?? block.status,
    blockType: overrides.blockType ?? block.blockType,
    note: overrides.note ?? block.note,
    source: overrides.source ?? block.source,
    sourceBookingId: overrides.sourceBookingId ?? block.sourceBookingId,
    sourceFptBookingId: overrides.sourceFptBookingId ?? block.sourceFptBookingId,
    reviewStatus: overrides.reviewStatus ?? block.reviewStatus,
    reviewNote: overrides.reviewNote ?? block.reviewNote,
    reviewedBy: overrides.reviewedBy ?? block.reviewedBy,
    reviewedAt: overrides.reviewedAt ?? block.reviewedAt,
  });

  const saveBlock = async (payload: Record<string, unknown>, block?: PlanningBlock | null) => {
    if (!user) return;
    const response = await fetch(block ? `/api/planning-blocks/${block.id}` : "/api/planning-blocks", {
      method: block ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.details?.join?.(" ") || "Failed to save planning block");
    }
    toast.success(block ? "Planning block updated" : "Planning block created");
    setDraftBlockDefaults(null);
    await loadData();
  };

  const deleteBlock = async (block: PlanningBlock) => {
    const response = await fetch(`/api/planning-blocks/${block.id}`, {
      method: "DELETE",
      headers,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Failed to delete planning block");
    }
    toast.success("Planning block deleted");
    await loadData();
  };

  const updatePlanningBlockStatus = async (block: PlanningBlock, status: PlanningBlockStatus) => {
    const response = await fetch(`/api/planning-blocks/${block.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(buildPlanningBlockPayload(block, { status })),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.details?.join?.(" ") || "Failed to update planning block status");
    }
    return data as PlanningBlock;
  };

  const confirmSelectedDeltas = async () => {
    if (!canManage || selectedDeltas.length === 0) return;
    const warningLabels = Array.from(new Set(selectedDeltas.flatMap(getDeltaWarningLabels)));
    if (warningLabels.length > 0) {
      const shouldConfirm = window.confirm(
        `These selected changes still have advisory warnings: ${warningLabels.join(", ")}.\n\nConfirm them internally anyway?`
      );
      if (!shouldConfirm) return;
    }

    try {
      await Promise.all(selectedDeltas.map((delta) => updatePlanningBlockStatus(delta.block, "confirmed")));
      toast.success(`${selectedDeltas.length} PlanningBlock change${selectedDeltas.length === 1 ? "" : "s"} confirmed internally`);
      clearDeltaSelection();
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to confirm selected changes");
    }
  };

  const moveDeltaToDraft = async (delta: PlanningDelta) => {
    if (!canManage) return;
    try {
      await updatePlanningBlockStatus(delta.block, "draft");
      toast.success("PlanningBlock moved back to draft");
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to move PlanningBlock back to draft");
    }
  };

  const updatePlanningBlockReview = async (block: PlanningBlock, reviewStatus: PlanningBlockReviewStatus, reviewNote = block.reviewNote) => {
    const reviewedAt = new Date().toISOString();
    const reviewedBy = user?.uid || user?.email || "unknown";
    const response = await fetch(`/api/planning-blocks/${block.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(buildPlanningBlockPayload(block, {
        reviewStatus,
        reviewNote,
        reviewedAt,
        reviewedBy,
      })),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.details?.join?.(" ") || "Failed to update review status");
    }
    return data as PlanningBlock;
  };

  const setConfirmedBlockReviewStatus = async (block: PlanningBlock, reviewStatus: PlanningBlockReviewStatus) => {
    if (!canManage) return;
    try {
      await updatePlanningBlockReview(block, reviewStatus);
      toast.success(`PlanningBlock marked ${reviewStatus.replace("_", " ")}`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update review status");
    }
  };

  const editConfirmedBlockReviewNote = async (block: PlanningBlock) => {
    if (!canManage) return;
    const nextNote = window.prompt("Review note", block.reviewNote || "");
    if (nextNote === null) return;
    try {
      await updatePlanningBlockReview(block, getBlockReviewStatus(block), nextNote);
      toast.success("Review note updated");
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update review note");
    }
  };

  const resetDeltaToBaseline = async (delta: PlanningDelta) => {
    if (!canManage) return;
    try {
      await saveBlock(buildBaselinePayloadFromBooking(delta.booking), delta.block);
    } catch (error: any) {
      toast.error(error?.message || "Failed to reset planning block");
    }
  };

  const deleteDeltaBlock = async (delta: PlanningDelta) => {
    if (!canManage) return;
    if (!window.confirm("Delete this proposed PlanningBlock? The source FPT booking will remain unchanged.")) return;
    try {
      await deleteBlock(delta.block);
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete planning block");
    }
  };

  const exportConfirmedScheduleCsv = () => {
    if (confirmedBlocks.length === 0) {
      toast.info("No confirmed PlanningBlocks match the current filters.");
      return;
    }

    const headers = [
      "project",
      "artist",
      "department",
      "startDate",
      "endDate",
      "allocationPercent",
      "status",
      "blockType",
      "source",
      "sourceBookingId",
      "sourceFptBookingId",
      "note",
    ];
    const rows = confirmedBlocks.map((block) => [
      getBlockProjectLabel(block),
      getArtistNameById(block.artistId),
      block.department || "Unassigned",
      block.startDate,
      block.endDate,
      block.allocationPercent,
      block.status,
      block.blockType,
      block.source,
      block.sourceBookingId || "",
      block.sourceFptBookingId || "",
      block.note || "",
    ]);
    const csv = [headers, ...rows].map((row) => row.map(escapeCsvValue).join(",")).join("\n");
    downloadTextFile(`confirmed-internal-schedule-${rangeStart}-to-${rangeEnd}.csv`, csv, "text/csv;charset=utf-8");
  };

  const confirmReadyPacketWarnings = () => {
    if (readyBlocks.length === 0) {
      toast.info("No ready PlanningBlocks match the current filters.");
      return false;
    }
    if (readyPacketWarnings.length === 0) return true;
    return window.confirm(
      `Ready schedule still has advisory warnings:\n\n${readyPacketWarnings.map((warning) => `${warning.label}: ${warning.count}`).join("\n")}\n\nExport anyway?`
    );
  };

  const getReadyPacketCsv = () => {
    const headers = [
      "projectName",
      "projectId",
      "department",
      "artistName",
      "artistId",
      "startDate",
      "endDate",
      "allocationPercent",
      "blockType",
      "status",
      "reviewStatus",
      "reviewNote",
      "source",
      "sourceBookingId",
      "sourceFptBookingId",
      "warningCount",
      "note",
    ];
    const rows = readyBlocks.map((block) => {
      const warnings = [
        ...getConfirmedBlockWarnings(block),
        ...(getDateRangeDays(block.startDate, block.endDate).some((day) => isWeekend(day)) ? ["Weekend included"] : []),
      ];
      return [
        getBlockProjectLabel(block),
        block.projectId || "",
        block.department || "Unassigned",
        getArtistNameById(block.artistId),
        block.artistId,
        block.startDate,
        block.endDate,
        block.allocationPercent,
        block.blockType,
        block.status,
        getBlockReviewStatus(block),
        block.reviewNote || "",
        block.source,
        block.sourceBookingId || "",
        block.sourceFptBookingId || "",
        warnings.length,
        block.note || "",
      ];
    });
    return [headers, ...rows].map((row) => row.map(escapeCsvValue).join(",")).join("\n");
  };

  const exportReadySchedulePacketCsv = () => {
    if (!confirmReadyPacketWarnings()) return;
    downloadTextFile(`ready-schedule-packet-${rangeStart}-to-${rangeEnd}.csv`, getReadyPacketCsv(), "text/csv;charset=utf-8");
  };

  const exportReadySchedulePacketJson = () => {
    if (!confirmReadyPacketWarnings()) return;
    downloadTextFile(
      `ready-schedule-packet-${rangeStart}-to-${rangeEnd}.json`,
      JSON.stringify(readySchedulePacket, null, 2),
      "application/json;charset=utf-8"
    );
  };

  const convertReadyBlocksToInternalBookings = async () => {
    if (!canManage) return;
    if (readyBlocks.length === 0) {
      toast.info("No ready PlanningBlocks match the current filters.");
      return;
    }
    const warningLabels = Array.from(new Set(readyBlocks.flatMap(getConfirmedBlockWarnings)));
    if (warningLabels.length > 0) {
      const shouldContinue = window.confirm(
        `Some ready PlanningBlocks still have advisory warnings: ${warningLabels.join(", ")}.\n\nCreate or update internal booking commitments anyway?`
      );
      if (!shouldContinue) return;
    }

    try {
      const response = await fetch("/api/internal-booking-commitments/convert-ready-planning-blocks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({ planningBlockIds: readyBlocks.map((block) => block.id) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to create internal booking commitments");
      }
      setConversionReport({
        createdCount: data.createdCount || 0,
        updatedCount: data.updatedCount || 0,
        skippedCount: data.skippedCount || 0,
        skipped: Array.isArray(data.skipped) ? data.skipped : [],
      });
      toast.success(`Internal bookings: ${data.createdCount || 0} created, ${data.updatedCount || 0} updated`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to create internal booking commitments");
    }
  };

  const exportInternalCommitmentsCsv = () => {
    if (visibleInternalCommitments.length === 0) {
      toast.info("No internal booking commitments match the current filters.");
      return;
    }
    const headers = [
      "projectName",
      "projectId",
      "department",
      "artistName",
      "artistId",
      "startDate",
      "endDate",
      "allocationPercent",
      "blockType",
      "commitmentStatus",
      "sourceSystem",
      "sourcePlanningBlockId",
      "sourceBookingId",
      "sourceFptBookingId",
      "note",
      "createdAt",
      "updatedAt",
    ];
    const rows = visibleInternalCommitments.map((commitment) => [
      commitment.projectName || "",
      commitment.projectId || "",
      commitment.department || "Unassigned",
      commitment.personName || "",
      commitment.personId,
      commitment.startDate,
      commitment.endDate,
      commitment.allocationPercent,
      commitment.blockType,
      commitment.commitmentStatus,
      commitment.sourceSystem,
      commitment.sourcePlanningBlockId,
      commitment.sourceBookingId || "",
      commitment.sourceFptBookingId || "",
      commitment.note || "",
      commitment.createdAt,
      commitment.updatedAt,
    ]);
    const csv = [headers, ...rows].map((row) => row.map(escapeCsvValue).join(",")).join("\n");
    downloadTextFile(`internal-booking-commitments-${rangeStart}-to-${rangeEnd}.csv`, csv, "text/csv;charset=utf-8");
  };

  const togglePublishCommitmentSelection = (commitmentId: string) => {
    if (!eligiblePublishCommitmentIds.has(commitmentId)) return;
    setSelectedCommitmentIds((current) => {
      const next = new Set(current);
      if (next.has(commitmentId)) next.delete(commitmentId);
      else next.add(commitmentId);
      return next;
    });
  };

  const selectAllPublishEligibleCommitments = () => {
    setSelectedCommitmentIds(new Set(Array.from(eligiblePublishCommitmentIds)));
  };

  const clearPublishCommitmentSelection = () => {
    setSelectedCommitmentIds(new Set());
    setDryRunReport(null);
    setPublishReport(null);
  };

  const toggleReconcileCommitmentSelection = (commitmentId: string) => {
    if (!eligibleReconcileCommitmentIds.has(commitmentId)) return;
    setRepublishDryRunReport(null);
    setRepublishReport(null);
    setSelectedReconcileCommitmentIds((current) => {
      const next = new Set(current);
      if (next.has(commitmentId)) next.delete(commitmentId);
      else next.add(commitmentId);
      return next;
    });
  };

  const selectAllReconcileEligibleCommitments = () => {
    setRepublishDryRunReport(null);
    setRepublishReport(null);
    setSelectedReconcileCommitmentIds(new Set(Array.from(eligibleReconcileCommitmentIds)));
  };

  const clearReconcileCommitmentSelection = () => {
    setSelectedReconcileCommitmentIds(new Set());
    setReconcileReport(null);
    setRepublishDryRunReport(null);
    setRepublishReport(null);
  };

  const runPublishDryRun = useCallback(async (commitmentIds: string[]) => {
    if (!canManage || commitmentIds.length === 0) {
      setDryRunReport(null);
      return;
    }
    setDryRunLoading(true);
    try {
      const response = await fetch("/api/internal-booking-commitments/dry-run-publish", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          commitmentIds,
          targets: ["fpt", "google_calendar"],
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to build dry-run publish preview");
      }
      setDryRunReport(data as DryRunPublishReport);
      setPublishReport(null);
    } catch (error: any) {
      setDryRunReport(null);
      toast.error(error?.message || "Failed to build dry-run publish preview");
    } finally {
      setDryRunLoading(false);
    }
  }, [canManage, headers]);

  useEffect(() => {
    if (!showPublishReadiness || !canManage) return;
    const commitmentIds = Array.from(selectedCommitmentIds);
    if (commitmentIds.length === 0) {
      setDryRunReport(null);
      return;
    }
    runPublishDryRun(commitmentIds);
  }, [canManage, runPublishDryRun, selectedCommitmentIds, showPublishReadiness]);

  const publishSelectedCommitmentsToFpt = async () => {
    if (!canManage || !dryRunReport?.dryRunToken) return;
    const commitmentIds = Array.from(selectedCommitmentIds);
    if (commitmentIds.length === 0) {
      toast.info("Select planned internal commitments before publishing.");
      return;
    }
    const shouldPublish = window.confirm(
      "This will write selected internal commitments to Flow Production Tracking Booking records. This cannot be automatically undone in this phase.\n\nPublish to Flow Production Tracking now?"
    );
    if (!shouldPublish) return;

    setPublishLoading(true);
    try {
      const response = await fetch("/api/internal-booking-commitments/publish", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          commitmentIds,
          dryRunToken: dryRunReport.dryRunToken,
          mode: "create_or_update",
          confirmed: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to publish internal commitments to Flow Production Tracking");
      }
      setPublishReport(data as PublishReport);
      setDryRunReport(null);
      toast.success(`Flow Production Tracking publish: ${data.summary?.created || 0} created, ${data.summary?.updated || 0} updated`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to publish internal commitments to Flow Production Tracking");
    } finally {
      setPublishLoading(false);
    }
  };

  const reconcileSelectedCommitmentsWithFpt = async () => {
    if (!canManage) return;
    const commitmentIds = Array.from(selectedReconcileCommitmentIds);
    if (commitmentIds.length === 0) {
      toast.info("Select published or Flow Production Tracking-linked commitments before reconciling.");
      return;
    }

    setReconcileLoading(true);
    try {
      const response = await fetch("/api/internal-booking-commitments/reconcile-fpt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({ commitmentIds }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to reconcile commitments with Flow Production Tracking");
      }
      setReconcileReport(data as ReconcileReport);
      setRepublishDryRunReport(null);
      setRepublishReport(null);
      toast.success(`Flow Production Tracking reconciliation: ${data.summary?.matched || 0} matched, ${data.summary?.drifted || 0} drifted`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to reconcile commitments with Flow Production Tracking");
    } finally {
      setReconcileLoading(false);
    }
  };

  const runRepublishUpdateDryRun = async () => {
    if (!canManage) return;
    const commitmentIds = Array.from(selectedReconcileCommitmentIds);
    if (commitmentIds.length === 0) {
      toast.info("Select Flow Production Tracking-linked commitments before running an update preview.");
      return;
    }
    setReconcileLoading(true);
    try {
      const response = await fetch("/api/internal-booking-commitments/dry-run-publish", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          commitmentIds,
          targets: ["fpt"],
          intent: "republish_update",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to build republish update preview");
      }
      setRepublishDryRunReport(data as DryRunPublishReport);
      setRepublishReport(null);
      toast.success(`Update preview ready: ${data.summary?.updateCount || 0} update payloads`);
    } catch (error: any) {
      setRepublishDryRunReport(null);
      toast.error(error?.message || "Failed to build republish update preview");
    } finally {
      setReconcileLoading(false);
    }
  };

  const republishSelectedCommitmentsToFpt = async () => {
    if (!canManage || !republishDryRunReport?.dryRunToken) return;
    const commitmentIds = Array.from(selectedReconcileCommitmentIds);
    const shouldPublish = window.confirm(
      "This will update existing Flow Production Tracking Booking records. It will not delete anything and cannot be automatically undone in this phase.\n\nRepublish updates to Flow Production Tracking now?"
    );
    if (!shouldPublish) return;

    setRepublishLoading(true);
    try {
      const response = await fetch("/api/internal-booking-commitments/republish-fpt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          commitmentIds,
          dryRunToken: republishDryRunReport.dryRunToken,
          confirmed: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to republish updates to Flow Production Tracking");
      }
      setRepublishReport(data as RepublishReport);
      setRepublishDryRunReport(null);
      toast.success(`Flow Production Tracking republish: ${data.summary?.updated || 0} updated`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to republish updates to Flow Production Tracking");
    } finally {
      setRepublishLoading(false);
    }
  };

  const exportDryRunJson = () => {
    if (!dryRunReport || dryRunResults.length === 0) {
      toast.info("Run a dry-run preview before exporting JSON.");
      return;
    }
    const payload = {
      metadata: {
        generatedAt: dryRunReport.generatedAt,
        exportedAt: new Date().toISOString(),
        generatedBy: user?.email || user?.uid || undefined,
        rangeStart,
        rangeEnd,
      },
      targets: dryRunReport.targets,
      summary: dryRunReport.summary,
      results: dryRunReport.results,
    };
    downloadTextFile(`internal-commitment-publish-dry-run-${rangeStart}-to-${rangeEnd}.json`, JSON.stringify(payload, null, 2), "application/json;charset=utf-8");
  };

  const updateInternalCommitmentStatus = async (commitment: InternalBookingCommitment, commitmentStatus: InternalBookingCommitmentStatus) => {
    if (!canManage) return;
    try {
      const response = await fetch(`/api/internal-booking-commitments/${encodeURIComponent(commitment.id)}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({ commitmentStatus }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to update internal commitment status");
      }
      toast.success(`Internal commitment marked ${commitmentStatus}`);
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update internal commitment status");
    }
  };

  const shiftRange = (daysToShift: number) => {
    setRangeStart((current) => addDays(current, daysToShift));
    setRangeEnd((current) => addDays(current, daysToShift));
  };

  const resetRange = () => {
    const start = startOfIsoWeek();
    setRangeStart(start);
    setRangeEnd(addDays(start, 27));
  };

  const getArtistBlocks = (artistId: string) => {
    return blocks
      .filter((block) => block.artistId === artistId && !block.isDeleted && blockOverlapsRange(block, calendarStart, calendarEnd))
      .sort((a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate));
  };

  const artistTimelineLanes = useMemo(() => {
    const laneMap = new Map<string, {
      laneCount: number;
      bookingLaneById: Map<string, number>;
      blockLaneById: Map<string, number>;
    }>();

    for (const artist of visibleArtists) {
      const timelineItems: TimelineLaneItem[] = [];
      const bookingLaneById = new Map<string, number>();
      const blockLaneById = new Map<string, number>();

      for (const booking of getArtistBookings(artist.id)) {
        const startIndex = Math.max(0, days.findIndex((day) => booking.startDate && day >= booking.startDate));
        const endIndex = days.reduce((last, day, dayIndex) => (booking.endDate && day <= booking.endDate ? dayIndex : last), -1);
        if (startIndex < 0 || endIndex < 0 || endIndex < startIndex) continue;
        timelineItems.push({
          id: booking.id,
          startIndex,
          endIndex,
          type: "booking",
        });
      }

      for (const block of getArtistBlocks(artist.id)) {
        const startIndex = Math.max(0, days.findIndex((day) => day >= block.startDate));
        const endIndex = days.reduce((last, day, dayIndex) => (day <= block.endDate ? dayIndex : last), -1);
        if (startIndex < 0 || endIndex < 0 || endIndex < startIndex) continue;
        timelineItems.push({
          id: block.id,
          startIndex,
          endIndex,
          type: "block",
        });
      }

      const assigned = assignTimelineLanes(timelineItems);
      for (const item of timelineItems) {
        const lane = assigned.laneById.get(item.id) ?? 0;
        if (item.type === "booking") {
          bookingLaneById.set(item.id, lane);
        } else {
          blockLaneById.set(item.id, lane);
        }
      }

      laneMap.set(artist.id, {
        laneCount: assigned.laneCount,
        bookingLaneById,
        blockLaneById,
      });
    }

    return laneMap;
  }, [blocks, calendarEnd, calendarStart, days, visibleArtists, visibleBookings]);

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
      <div className="border-b border-slate-200 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-950">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <CalendarDays className="h-4 w-4" />
              Artist Blocking
            </div>
            <h1 className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">{formatRangeLabel(rangeStart, rangeEnd)}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-1.5 dark:border-slate-800 dark:bg-slate-900/60">
            <button onClick={() => shiftRange(-28)} className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={resetRange} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">Today</button>
            <button onClick={() => shiftRange(28)} className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button onClick={loadData} className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              <RefreshCw className="h-4 w-4" />
            </button>
            {canManage ? (
              <button onClick={openCreateModal} className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950">
                <Plus className="h-4 w-4" />
                New block
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900/40 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">Start</span>
            <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900" />
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">End</span>
            <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900" />
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">Department</span>
            <select value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900">
              <option value="all">All departments</option>
              {departments.map((department) => (
                <option key={department} value={department}>{department}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">FPT user</span>
            <select
              value={artistFptStatusFilter}
              onChange={(event) => setArtistFptStatusFilter(event.target.value)}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs capitalize dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="active">Active</option>
              <option value="all">All linked</option>
              <option value="inactive">Inactive</option>
              <option value="unknown">Unknown</option>
              {fptStatusOptions
                .filter((status) => !["active", "inactive", "unknown"].includes(status))
                .map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">Project</span>
            <select value={projectFilter} onChange={(event) => setProjectFilter(event.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900">
              <option value="all">All projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">Status</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "all" | PlanningBlockStatus)} className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs capitalize dark:border-slate-700 dark:bg-slate-900">
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status === "all" ? "All statuses" : status}</option>
              ))}
            </select>
          </label>
          <label className="inline-flex min-h-8 items-center gap-2 self-end rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
            <input
              type="checkbox"
              checked={showFptBookings}
              onChange={(event) => setShowFptBookings(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Show FPT bookings
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-slate-500">FPT resolution</span>
            <select
              value={fptBookingResolutionFilter}
              onChange={(event) => setFptBookingResolutionFilter(event.target.value as FptBookingResolutionFilter)}
              disabled={!showFptBookings}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:disabled:bg-slate-900/50"
            >
              <option value="all">All FPT bookings</option>
              <option value="unmapped">Unmapped only</option>
              <option value="manual">Manually linked</option>
              <option value="automatic">Auto resolved</option>
            </select>
          </label>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-100">
          <div className="min-w-0">
            <span className="font-semibold">FPT baseline</span>
            <span className="ml-2 hidden text-blue-800/75 dark:text-blue-200/75 xl:inline">
              Imported FPT bookings stay read-only and appear before planning blocks.
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={syncFptPeopleStatuses}
              disabled={!canManage || fptStatusSyncLoading}
              className="rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-100 dark:hover:bg-blue-950/50"
            >
              {fptStatusSyncLoading ? "Syncing..." : "Sync FPT statuses"}
            </button>
            <button
              type="button"
              onClick={syncFptBookingBaseline}
              disabled={!canManage || bookingSyncLoading}
              className="rounded-md bg-blue-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-400 dark:text-blue-950 dark:hover:bg-blue-300"
            >
              {bookingSyncLoading ? "Refreshing..." : "Refresh FPT baseline"}
            </button>
          </div>
        </div>

        {showFptBookings && unmappedBookings.length > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/70 dark:bg-amber-950/25">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-200" />
                <div>
                  <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">Unmapped Flow Production Tracking bookings</div>
                  <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                    These Flow Production Tracking bookings could not be placed on artist rows because no local personId was resolved.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowUnmappedBookings((current) => !current)}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-slate-950 dark:text-amber-100 dark:hover:bg-amber-900/30"
              >
                <ChevronDown className={cn("h-4 w-4 transition-transform", !showUnmappedBookings && "-rotate-90")} />
                {showUnmappedBookings ? "Collapse" : "Expand"}
              </button>
            </div>
            {showUnmappedBookings ? (
              <>
                <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {unmappedBookings.slice(0, 9).map((booking) => (
                    <div key={booking.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm dark:border-amber-900/60 dark:bg-slate-950 dark:text-slate-200">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-semibold">{getBookingLabel(booking)}</span>
                        <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-900/50 dark:text-amber-100">{booking.bookingStatus || "FPT"}</span>
                      </div>
                      <div className="mt-1 text-slate-500 dark:text-slate-400">{getUnmappedBookingPersonLabel(booking)}</div>
                      <div className="mt-1">{booking.startDate || "No start"} to {booking.endDate || "No end"} · {getBookingAllocation(booking)}%</div>
                      {booking.note ? <div className="mt-1 truncate text-slate-500 dark:text-slate-400">Note: {booking.note}</div> : null}
                      {canManage ? (
                        <button
                          type="button"
                          onClick={() => openLinkBookingModal(booking)}
                          className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                        >
                          Link to artist
                        </button>
                      ) : (
                        <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">Read-only. Calendar manage permission required to link.</div>
                      )}
                    </div>
                  ))}
                </div>
                {unmappedBookings.length > 9 ? (
                  <div className="mt-2 text-xs text-amber-800 dark:text-amber-200">Showing 9 of {unmappedBookings.length} unmapped bookings in this filtered range.</div>
                ) : null}
              </>
            ) : (
              <div className="mt-3 text-xs text-amber-800 dark:text-amber-200">
                {unmappedBookings.length} unmapped booking{unmappedBookings.length === 1 ? "" : "s"} hidden.
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex h-full items-center justify-center text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading schedule
          </div>
        ) : blockingDepartmentGroups.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
            No artists match the current filters.
          </div>
        ) : (
          <div className="min-w-max px-3 pb-4">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
              <div
                className="sticky top-0 z-30 grid border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
                style={{ gridTemplateColumns: `${ARTIST_COLUMN_WIDTH}px repeat(${days.length}, ${dayColumnWidth}px)` }}
              >
                <div className="sticky left-0 z-40 flex h-11 items-center border-r border-slate-200 bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:border-slate-800 dark:bg-slate-950">
                  Timeline
                </div>
                {visibleMonthMarkers.map((marker) => (
                  <div
                    key={marker.monthKey}
                    className="flex h-11 items-center border-r border-slate-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 last:border-r-0 dark:border-slate-800 dark:text-slate-400"
                    style={{ gridColumn: `${marker.startIndex + 2} / span ${marker.span}` }}
                  >
                    {marker.label}
                  </div>
                ))}
                <div className="sticky left-0 top-11 z-40 flex h-14 items-center border-r border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100">
                  Artist
                </div>
                {days.map((day) => {
                  const outsideTargetRange = day < rangeStart || day > rangeEnd;
                  return (
                    <div
                      key={day}
                      className={cn(
                        "flex h-14 flex-col items-center justify-center border-r border-t border-slate-200 px-0.5 text-center dark:border-slate-800",
                        outsideTargetRange && "bg-slate-50/70 text-slate-400 dark:bg-slate-900/50 dark:text-slate-600",
                        isWeekend(day) && "bg-slate-300/55 text-slate-500 dark:bg-slate-700/90",
                        day === today && "bg-yellow-50 text-slate-900 dark:bg-yellow-500/10 dark:text-yellow-100"
                      )}
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        {new Intl.DateTimeFormat("en-GB", { weekday: "short" }).format(new Date(`${day}T00:00:00.000Z`))}
                      </span>
                      <span className="mt-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {new Intl.DateTimeFormat("en-GB", { day: "2-digit" }).format(new Date(`${day}T00:00:00.000Z`))}
                      </span>
                    </div>
                  );
                })}
              </div>

              {blockingDepartmentGroups.map((group) => {
                const departmentColor = getDepartmentColor(group.department);
                const collapsed = collapsedDepartments[group.department] ?? false;
                const departmentCost = Math.round(group.rows.reduce((sum, row) => sum + row.bookings.reduce((rowSum, booking) => rowSum + (booking.allocationPercent / 100) * booking.versionDays.size * 100, 0), 0) * 100) / 100;
                return (
                  <div key={group.department}>
                    <div className="flex border-y border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/60">
                      <div
                        className="sticky left-0 z-20 flex h-12 items-center gap-3 border-r border-slate-200 bg-slate-50/80 px-4 dark:border-slate-700 dark:bg-slate-800/60"
                        style={{ width: ARTIST_COLUMN_WIDTH, minWidth: ARTIST_COLUMN_WIDTH }}
                      >
                        <button
                          type="button"
                          onClick={() => setCollapsedDepartments((current) => ({ ...current, [group.department]: !collapsed }))}
                          className="inline-flex h-5 w-5 items-center justify-center text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                          aria-label={collapsed ? `Expand ${group.department}` : `Collapse ${group.department}`}
                        >
                          <ChevronDown className={cn("h-4 w-4 transition-transform", collapsed && "-rotate-90")} />
                        </button>
                        <span className="inline-flex items-center justify-center text-slate-400 dark:text-slate-500">
                          <Users className="h-5 w-5" />
                        </span>
                        <span
                          className="app-pill inline-flex items-center whitespace-nowrap px-2 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-[0.14em]"
                          style={{
                            backgroundColor: rgba(departmentColor, 0.18),
                            color: "#0f172a",
                          }}
                        >
                          {group.department}
                        </span>
                        <span className="whitespace-nowrap text-sm font-medium text-slate-500 dark:text-slate-300">
                          {group.rows.length} artist{group.rows.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div
                        className="relative flex items-center"
                        style={{ width: `${days.length * dayColumnWidth}px`, minWidth: `${days.length * dayColumnWidth}px` }}
                      >
                        <div className="sticky right-0 z-10 ml-auto flex items-center gap-6 bg-slate-50/95 py-3 pl-6 pr-4 text-right backdrop-blur-sm dark:bg-slate-800/95">
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Cost</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">£{departmentCost.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!collapsed
                      ? group.rows.map((row) => (
                          <div
                            key={row.artist.id}
                            className="grid border-b border-slate-200 dark:border-slate-800"
                            style={{ gridTemplateColumns: `${ARTIST_COLUMN_WIDTH}px repeat(${days.length}, ${dayColumnWidth}px)` }}
                          >
                            <div className="sticky left-0 z-10 flex items-center gap-3 border-r border-slate-200 bg-white px-4 py-2 dark:border-slate-800 dark:bg-slate-950">
                              {row.artist.photoUrl ? (
                                <img src={row.artist.photoUrl} alt={row.artist.name} className="h-10 w-10 rounded-lg object-cover" />
                              ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                                  {initialsForName(row.artist.name)}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="truncate text-[14px] font-semibold text-slate-900 dark:text-slate-100">{row.artist.name}</div>
                                <div className="truncate text-[12px] text-slate-500 dark:text-slate-400">{row.artist.roleTitle || row.department}</div>
                              </div>
                            </div>
                            <div
                              className="relative"
                              style={{ gridColumn: `2 / span ${days.length}`, width: `${days.length * dayColumnWidth}px`, minHeight: "64px" }}
                            >
                              <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${days.length}, ${dayColumnWidth}px)` }}>
                                {days.map((day) => {
                                  const outsideTargetRange = day < rangeStart || day > rangeEnd;
                                  return (
                                    <div
                                      key={day}
                                      className={cn(
                                        "border-r border-slate-100 dark:border-slate-900",
                                        outsideTargetRange && "bg-slate-50/70 dark:bg-slate-950/40",
                                        isWeekend(day) && "bg-slate-300/45 dark:bg-slate-700/70",
                                        day === today && "bg-yellow-50 dark:bg-yellow-500/10"
                                      )}
                                    />
                                  );
                                })}
                              </div>

                              {row.bookings.map((booking) => (
                                <div
                                  key={booking.id}
                                  className="absolute top-1/2 -translate-y-1/2 overflow-hidden rounded-xl border shadow-sm"
                                  style={{
                                    left: `${booking.startIndex * dayColumnWidth}px`,
                                    width: `${(booking.endIndex - booking.startIndex + 1) * dayColumnWidth}px`,
                                    borderColor: rgba(booking.projectColor, 0.45),
                                    backgroundColor: rgba(booking.projectColor, 0.15),
                                  }}
                                  title={`${booking.projectName} | ${formatRangeLabel(booking.startDate, booking.endDate)} | ${booking.allocationPercent}%`}
                                >
                                  <div
                                    className="grid h-8"
                                    style={{ gridTemplateColumns: `repeat(${booking.endIndex - booking.startIndex + 1}, ${dayColumnWidth}px)` }}
                                  >
                                    {days.slice(booking.startIndex, booking.endIndex + 1).map((day) => {
                                      const hasVersion = booking.versionDays.has(day);
                                      return (
                                        <div
                                          key={`${booking.id}:${day}`}
                                          className={cn("h-full", isWeekend(day) && "bg-white/80 dark:bg-slate-950/60")}
                                          style={{
                                            backgroundColor: isWeekend(day)
                                              ? undefined
                                              : hasVersion
                                                ? rgba(booking.projectColor, 0.6)
                                                : rgba(booking.projectColor, 0.15),
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  <div className="absolute inset-y-0 left-3 right-3 flex items-center justify-between gap-2 overflow-hidden text-xs font-semibold text-slate-900">
                                    <span className="truncate">{booking.projectName}</span>
                                    <span className="shrink-0 opacity-80">{booking.allocationPercent}%</span>
                                  </div>
                                </div>
                              ))}

                              {row.outsideVersionDays.map((entry) => {
                                const dayIndex = days.findIndex((day) => day === entry.day);
                                if (dayIndex < 0) return null;
                                return (
                                  <div
                                    key={`${row.artist.id}:${entry.projectKey}:${entry.day}`}
                                    className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-[4px] shadow-sm"
                                    style={{
                                      left: `${dayIndex * dayColumnWidth + dayColumnWidth / 2}px`,
                                      backgroundColor: rgba(entry.color, 0.92),
                                    }}
                                    title={`Published work on ${formatCompactDateRange(entry.day, entry.day)} without a booking`}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))
                      : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedConflictArtist ? (
        <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  Conflict Details
                </div>
                <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">{selectedConflictArtist.name}</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {selectedConflictArtist.department || "Unassigned"} · {selectedConflictDays.length} affected day{selectedConflictDays.length === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedConflictArtistId(null)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-5 py-4">
            {selectedConflictDays.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                No active conflicts in the current filtered range.
              </div>
            ) : (
              <div className="space-y-4">
                {selectedConflictDays.map((conflictDay) => (
                  <div key={conflictDay.date} className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/70 dark:bg-amber-950/20">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-950 dark:text-slate-100">{formatDayLabel(conflictDay.date)}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {conflictDay.conflictTypes.map((type) => (
                            <span key={type} className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/60 dark:text-amber-100">{type}</span>
                          ))}
                          {conflictDay.isWeekend ? <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">Weekend</span> : null}
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-600 dark:text-slate-300">
                        <div>Total allocation: <span className="font-semibold">{conflictDay.totalAllocation}%</span></div>
                        <div>Available: <span className="font-semibold">{conflictDay.availableCapacity}%</span></div>
                        <div className={conflictDay.overAllocatedBy > 0 ? "font-semibold text-red-700 dark:text-red-300" : ""}>
                          {conflictDay.overAllocatedBy > 0 ? `Over by ${conflictDay.overAllocatedBy}%` : `Remaining ${conflictDay.availableCapacity - conflictDay.totalAllocation}%`}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg bg-white px-3 py-2 dark:bg-slate-950">
                        <div className="text-slate-500">Planning</div>
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{conflictDay.planningAllocation}%</div>
                      </div>
                      <div className="rounded-lg bg-white px-3 py-2 dark:bg-slate-950">
                        <div className="text-slate-500">FPT bookings</div>
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{conflictDay.fptAllocation}%</div>
                      </div>
                      <div className="rounded-lg bg-white px-3 py-2 dark:bg-slate-950">
                        <div className="text-slate-500">Capacity</div>
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{conflictDay.availableCapacity}%</div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      {conflictDay.planningBlocks.map((block) => (
                        <div key={block.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm dark:border-slate-800 dark:bg-slate-950">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">PlanningBlock · editable{block.source === "fpt_booking" ? " · copied from FPT" : ""}</div>
                              <div className="mt-1 truncate font-semibold text-slate-900 dark:text-slate-100">{getBlockLabel(block)}</div>
                              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{block.startDate} to {block.endDate} · {block.allocationPercent}% · {block.status}</div>
                              {block.source === "fpt_booking" && findSourceBooking(block) && !isUnchangedFptCopy(block) ? (
                                <div className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-200">Differs from source FPT booking; counted as proposed planning delta.</div>
                              ) : null}
                            </div>
                            {canManage ? (
                              <button
                                type="button"
                                onClick={() => editConflictPlanningBlock(block)}
                                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                              >
                                <Pencil className="h-3 w-3" />
                                Edit planning block
                              </button>
                            ) : (
                              <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">Read-only</span>
                            )}
                          </div>
                          {block.note ? <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">{block.note}</div> : null}
                        </div>
                      ))}

                      {[...conflictDay.fptBookings, ...conflictDay.vacationBookings].map((booking) => (
                        <div key={booking.id} className="rounded-lg border border-blue-200 bg-white p-3 text-sm dark:border-blue-900/70 dark:bg-slate-950">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
                                {booking.isVacation ? "FPT vacation booking" : "FPT booking"} · read-only · {getBookingResolutionLabel(booking)}
                              </div>
                              <div className="mt-1 truncate font-semibold text-slate-900 dark:text-slate-100">{getBookingLabel(booking)}</div>
                              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {booking.startDate || "No start"} to {booking.endDate || "No end"} · {getBookingAllocation(booking)}% · {booking.bookingStatus || "No status"}
                              </div>
                            </div>
                            {canManage && booking.resolutionSource === "manual" ? (
                              <div className="flex shrink-0 flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => createPlanningBlockFromBooking(booking)}
                                  className="rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/60"
                                >
                                  Create planning block from booking
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openLinkBookingModal(booking)}
                                  className="rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-900/60"
                                >
                                  Relink / undo
                                </button>
                              </div>
                            ) : (
                              <div className="flex shrink-0 flex-col gap-1">
                                {canManage ? (
                                  <button
                                    type="button"
                                    onClick={() => createPlanningBlockFromBooking(booking)}
                                    className="rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/60"
                                  >
                                    Create planning block from booking
                                  </button>
                                ) : null}
                                <span className="rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-200">Read-only FPT booking</span>
                              </div>
                            )}
                          </div>
                          {booking.note ? <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">{booking.note}</div> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showDeltaReview ? (
        <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-2xl flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <CalendarDays className="h-4 w-4" />
                  Changes from FPT baseline
                </div>
                <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">Scenario changes</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {deltaSummary.count} proposed change{deltaSummary.count === 1 ? "" : "s"} · {formatCapacityDays(deltaSummary.changedArtistDays)} changed · {deltaSummary.warningCount} with warnings
                </p>
                {canManage && planningDeltas.length > 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllDeltas}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={clearDeltaSelection}
                      disabled={selectedDeltaBlockIds.size === 0}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={confirmSelectedDeltas}
                      disabled={selectedDeltas.length === 0}
                      className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400"
                    >
                      Confirm selected changes
                    </button>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {selectedDeltas.length} selected
                    </span>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setShowDeltaReview(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-5 py-4">
            {planningDeltas.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                Exact FPT copies are hidden here. No changed FPT-derived PlanningBlocks match the current filters.
              </div>
            ) : (
              <div className="space-y-5">
                {deltaGroups.map((projectGroup) => (
                  <div key={projectGroup.projectName} className="space-y-3">
                    <div className="sticky top-0 z-10 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                      {projectGroup.projectName}
                    </div>
                    {projectGroup.artistGroups.map((artistGroup) => (
                      <div key={`${projectGroup.projectName}-${artistGroup.artistName}`} className="space-y-2">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{artistGroup.artistName}</div>
                        {artistGroup.deltas.map((delta) => {
                          const sourceArtist = getArtistNameById(delta.booking.personId) || delta.booking.personName || getUnmappedBookingPersonLabel(delta.booking);
                          const sourceProject = getBookingLabel(delta.booking);
                          const proposedProject = getBlockProjectLabel(delta.block);
                          const deltaWarnings = getDeltaWarningLabels(delta);
                          const isSelected = selectedDeltaBlockIds.has(delta.block.id);
                          return (
                            <div key={delta.block.id} className={cn("rounded-lg border bg-white p-4 shadow-sm dark:bg-slate-950", delta.hasCapacityWarning ? "border-amber-200 dark:border-amber-900/70" : "border-slate-200 dark:border-slate-800")}>
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex min-w-0 gap-3">
                                  {canManage ? (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleDeltaSelection(delta.block.id)}
                                      className="mt-1 h-4 w-4 rounded border-slate-300"
                                      aria-label={`Select ${getBlockLabel(delta.block)} scenario change`}
                                    />
                                  ) : null}
                                  <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">PlanningBlock · copied from FPT</span>
                                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium capitalize", delta.block.status === "confirmed" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100" : "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300")}>{delta.block.status}</span>
                                    {delta.hasCapacityWarning ? (
                                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/60 dark:text-amber-100">Capacity warning</span>
                                    ) : null}
                                    {deltaWarnings.map((warning) => (
                                      <span key={warning} className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-200">{warning}</span>
                                    ))}
                                  </div>
                                  <div className="mt-1 truncate text-sm font-semibold text-slate-950 dark:text-slate-100">{getBlockLabel(delta.block)}</div>
                                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    Source booking {delta.booking.fptBookingId || delta.booking.id} remains read-only. Reset only updates this PlanningBlock.
                                  </div>
                                  </div>
                                </div>
                                {canManage ? (
                                  <div className="flex shrink-0 flex-wrap gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setShowDeltaReview(false);
                                        openEditModal(delta.block);
                                      }}
                                      className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                                    >
                                      Edit proposed
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => resetDeltaToBaseline(delta)}
                                      className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-900/70 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-900/60"
                                    >
                                      Reset to FPT
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => deleteDeltaBlock(delta)}
                                      className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-200 dark:hover:bg-red-900/40"
                                    >
                                      Delete
                                    </button>
                                    {delta.block.status === "confirmed" ? (
                                      <button
                                        type="button"
                                        onClick={() => moveDeltaToDraft(delta)}
                                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                                      >
                                        Move back to draft
                                      </button>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">Read-only</span>
                                )}
                              </div>

                              <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/70 dark:bg-blue-950/30">
                                  <div className="font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-200">FPT baseline</div>
                                  <div className="mt-1 text-slate-700 dark:text-slate-200">{sourceProject}</div>
                                  <div className="mt-1 text-slate-500 dark:text-slate-400">{sourceArtist} · {delta.booking.department || "Unassigned"}</div>
                                  <div className="mt-1 text-slate-500 dark:text-slate-400">{delta.booking.startDate || "No start"} to {delta.booking.endDate || "No end"} · {getBookingAllocation(delta.booking)}% · {delta.booking.bookingStatus || "No status"}</div>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
                                  <div className="font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">Proposed plan</div>
                                  <div className="mt-1 text-slate-700 dark:text-slate-200">{proposedProject}</div>
                                  <div className="mt-1 text-slate-500 dark:text-slate-400">{delta.artistName} · {delta.block.department || "Unassigned"}</div>
                                  <div className="mt-1 text-slate-500 dark:text-slate-400">{delta.block.startDate} to {delta.block.endDate} · {delta.block.allocationPercent}% · {delta.block.status}</div>
                                </div>
                              </div>

                              <div className="mt-3 space-y-1.5">
                                {delta.changedFields.map((field) => (
                                  <div key={`${delta.block.id}-${field.label}`} className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs dark:bg-slate-900">
                                    <span className="font-semibold text-slate-700 dark:text-slate-200">{field.label}</span>
                                    <span className="text-right text-slate-600 dark:text-slate-300">
                                      <span className="line-through opacity-70">{field.before}</span>
                                      <span className="px-1.5">-&gt;</span>
                                      <span className="font-semibold">{field.after}</span>
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {delta.block.note ? <div className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">{delta.block.note}</div> : null}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showConfirmedSchedule ? (
        <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-3xl flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  <CalendarDays className="h-4 w-4" />
                  Internal schedule
                </div>
                <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">Confirmed schedule</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {confirmedSummary.blockCount} confirmed · {confirmedSummary.readyCount} ready · {confirmedSummary.blockedCount} blocked · {confirmedSummary.needsReviewCount} needs review · {confirmedSummary.warningCount} warning{confirmedSummary.warningCount === 1 ? "" : "s"}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {readyCommitmentSummary.committedCount} ready block{readyCommitmentSummary.committedCount === 1 ? "" : "s"} already have internal booking commitments.
                </p>
                <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  Review filter
                  <select
                    value={reviewFilter}
                    onChange={(event) => setReviewFilter(event.target.value as ReviewFilter)}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                  >
                    <option value="all">All</option>
                    <option value="needs_review">Needs review</option>
                    <option value="ready">Ready</option>
                    <option value="blocked">Blocked</option>
                    <option value="warnings">Warnings</option>
                  </select>
                </label>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={exportReadySchedulePacketCsv}
                  disabled={readyPacketTotals.blockCount === 0}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-800 dark:bg-slate-950 dark:text-emerald-100 dark:hover:bg-emerald-950/40"
                >
                  Export ready CSV
                </button>
                {canManage ? (
                  <button
                    type="button"
                    onClick={convertReadyBlocksToInternalBookings}
                    disabled={readyCommitmentSummary.eligibleCount === 0}
                    className="rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                  >
                    Create internal bookings
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={exportReadySchedulePacketJson}
                  disabled={readyPacketTotals.blockCount === 0}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-800 dark:bg-slate-950 dark:text-emerald-100 dark:hover:bg-emerald-950/40"
                >
                  Export ready JSON
                </button>
                <button
                  type="button"
                  onClick={exportConfirmedScheduleCsv}
                  disabled={confirmedSummary.blockCount === 0}
                  className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400"
                >
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirmedSchedule(false)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-5 py-4">
            {confirmedBlocks.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                No confirmed PlanningBlocks match the current filters. Confirmed schedule uses the visible date range, project, department, and status filters.
              </div>
            ) : filteredConfirmedBlocks.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                No confirmed PlanningBlocks match the selected review filter.
              </div>
            ) : (
              <div className="space-y-5">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/70 dark:bg-emerald-950/25">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Ready schedule packet</div>
                      <div className="mt-1 text-sm text-emerald-950 dark:text-emerald-100">
                        {readyPacketTotals.blockCount} ready block{readyPacketTotals.blockCount === 1 ? "" : "s"} · {formatCapacityDays(readyPacketTotals.totalArtistDays)} · {readyPacketTotals.fptDerivedCount} from FPT · {readyPacketTotals.warningsCount} warning{readyPacketTotals.warningsCount === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={exportReadySchedulePacketCsv}
                        disabled={readyPacketTotals.blockCount === 0}
                        className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400"
                      >
                        Export ready schedule packet CSV
                      </button>
                      <button
                        type="button"
                        onClick={exportReadySchedulePacketJson}
                        disabled={readyPacketTotals.blockCount === 0}
                        className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-800 dark:bg-slate-950 dark:text-emerald-100 dark:hover:bg-emerald-950/40"
                      >
                        Export JSON
                      </button>
                    </div>
                  </div>
                  {readyPacketWarnings.length > 0 ? (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-100">
                      Ready packet advisory warnings: {readyPacketWarnings.map((warning) => `${warning.label} (${warning.count})`).join(", ")}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Internal booking commitments</div>
                      <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                        {readyCommitmentSummary.eligibleCount} eligible ready block{readyCommitmentSummary.eligibleCount === 1 ? "" : "s"} · {readyCommitmentSummary.committedCount} committed · {readyCommitmentSummary.uncommittedCount} not committed
                      </div>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Creates app-owned internal commitments only. Imported FPT bookings stay read-only and unchanged.
                      </p>
                    </div>
                    {canManage ? (
                      <button
                        type="button"
                        onClick={convertReadyBlocksToInternalBookings}
                        disabled={readyCommitmentSummary.eligibleCount === 0}
                        className="rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                      >
                        Create internal bookings from ready blocks
                      </button>
                    ) : null}
                  </div>
                  {conversionReport ? (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                      <div className="font-semibold">
                        Last conversion: {conversionReport.createdCount} created, {conversionReport.updatedCount} updated, {conversionReport.skippedCount} skipped.
                      </div>
                      {conversionReport.skipped.length > 0 ? (
                        <div className="mt-1 space-y-1">
                          {conversionReport.skipped.slice(0, 5).map((skip) => (
                            <div key={`${skip.planningBlockId}-${skip.reason}`}>{skip.planningBlockId}: {skip.reason}</div>
                          ))}
                          {conversionReport.skipped.length > 5 ? <div>+{conversionReport.skipped.length - 5} more skipped</div> : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-3 md:grid-cols-5">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/70 dark:bg-emerald-950/25">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Total confirmed</div>
                    <div className="mt-1 text-sm font-semibold text-emerald-950 dark:text-emerald-100">{formatCapacityDays(confirmedSummary.totalArtistDays)}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Blocks</div>
                    <div className="mt-1 text-sm font-semibold text-slate-950 dark:text-slate-100">{confirmedSummary.blockCount}</div>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/70 dark:bg-blue-950/30">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">From FPT bookings</div>
                    <div className="mt-1 text-sm font-semibold text-blue-950 dark:text-blue-100">{confirmedSummary.fptDerivedCount}</div>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-white p-3 dark:border-emerald-900/70 dark:bg-slate-950">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Ready</div>
                    <div className="mt-1 text-sm font-semibold text-emerald-950 dark:text-emerald-100">{confirmedSummary.readyCount}</div>
                  </div>
                  <div className={cn("rounded-lg border p-3", confirmedSummary.warningCount > 0 ? "border-amber-200 bg-amber-50 dark:border-amber-900/70 dark:bg-amber-950/25" : "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900")}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Warnings</div>
                    <div className={cn("mt-1 text-sm font-semibold", confirmedSummary.warningCount > 0 ? "text-amber-900 dark:text-amber-100" : "text-slate-950 dark:text-slate-100")}>{confirmedSummary.warningCount}</div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">By department</div>
                    <div className="mt-2 space-y-1.5">
                      {confirmedSummary.byDepartment.map((item) => (
                        <div key={item.name} className="flex items-center justify-between gap-3 text-xs">
                          <span className="truncate text-slate-700 dark:text-slate-200">{item.name}</span>
                          <span className="shrink-0 font-semibold text-slate-900 dark:text-slate-100">{formatCapacityDays(item.artistDays)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">By project</div>
                    <div className="mt-2 space-y-1.5">
                      {confirmedSummary.byProject.map((item) => (
                        <div key={item.name} className="flex items-center justify-between gap-3 text-xs">
                          <span className="truncate text-slate-700 dark:text-slate-200">{item.name}</span>
                          <span className="shrink-0 font-semibold text-slate-900 dark:text-slate-100">{formatCapacityDays(item.artistDays)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {confirmedScheduleGroups.map((projectGroup) => (
                  <div key={projectGroup.projectName} className="space-y-3">
                    <div className="sticky top-0 z-10 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:border-emerald-900/70 dark:bg-emerald-950 dark:text-emerald-100">
                      {projectGroup.projectName}
                    </div>
                    {projectGroup.departmentGroups.map((departmentGroup) => (
                      <div key={`${projectGroup.projectName}-${departmentGroup.department}`} className="space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{departmentGroup.department}</div>
                        {departmentGroup.artistGroups.map((artistGroup) => (
                          <div key={`${projectGroup.projectName}-${departmentGroup.department}-${artistGroup.artistName}`} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                            <div className="text-sm font-semibold text-slate-950 dark:text-slate-100">{artistGroup.artistName}</div>
                            <div className="mt-2 space-y-2">
                              {artistGroup.blocks.map((block) => {
                                const reviewStatus = getBlockReviewStatus(block);
                                const checklist = getConfirmedBlockChecklist(block);
                                const warnings = checklist.filter((item) => !item.ok);
                                const commitment = commitmentByPlanningBlockId.get(block.id);
                                return (
                                  <div key={block.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-900">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="font-semibold text-slate-900 dark:text-slate-100">{getBlockProjectLabel(block)}</div>
                                        <div className="mt-1 text-slate-500 dark:text-slate-400">
                                          {block.startDate} to {block.endDate} · {block.allocationPercent}% · {block.blockType} · {block.source}
                                        </div>
                                        <div className="mt-1 text-slate-500 dark:text-slate-400">
                                          {block.department || "Unassigned"} · {formatCapacityDays(getConfirmedBlockArtistDays(block))}
                                        </div>
                                      </div>
                                      <div className="flex shrink-0 flex-col items-end gap-1">
                                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100">Confirmed</span>
                                        <span className={cn(
                                          "rounded-full px-2 py-0.5 text-[11px] font-medium capitalize",
                                          reviewStatus === "ready" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
                                          reviewStatus === "blocked" && "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100",
                                          reviewStatus === "needs_review" && "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100"
                                        )}>{reviewStatus.replace("_", " ")}</span>
                                        {block.source === "fpt_booking" || block.sourceBookingId || block.sourceFptBookingId ? (
                                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 dark:bg-blue-900/60 dark:text-blue-100">From FPT</span>
                                        ) : null}
                                        {commitment ? (
                                          <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white dark:bg-slate-100 dark:text-slate-950">Internal booking</span>
                                        ) : null}
                                      </div>
                                    </div>

                                    <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
                                      {checklist.map((item) => (
                                        <div key={`${block.id}-${item.label}`} className={cn("flex items-center gap-2 rounded px-2 py-1", item.ok ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100" : "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-100")}>
                                          <span className={cn("h-2 w-2 rounded-full", item.ok ? "bg-emerald-500" : "bg-amber-500")} />
                                          {item.label}
                                        </div>
                                      ))}
                                    </div>

                                    {warnings.length > 0 ? (
                                      <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                                        Advisory warnings: {warnings.map((item) => item.label).join(", ")}
                                      </div>
                                    ) : null}
                                    {block.note ? <div className="mt-2 rounded bg-white px-2 py-1 text-slate-600 dark:bg-slate-950 dark:text-slate-300">{block.note}</div> : null}
                                    {block.reviewNote ? <div className="mt-2 rounded bg-white px-2 py-1 text-slate-600 dark:bg-slate-950 dark:text-slate-300">Review note: {block.reviewNote}</div> : null}
                                    {commitment ? <div className="mt-2 rounded bg-slate-900 px-2 py-1 text-white dark:bg-slate-100 dark:text-slate-950">Internal commitment: {commitment.commitmentStatus} · updated {new Date(commitment.updatedAt).toLocaleString()}</div> : null}
                                    {block.reviewedAt ? <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">Last reviewed {new Date(block.reviewedAt).toLocaleString()} by {block.reviewedBy || "unknown"}</div> : null}

                                    {canManage ? (
                                      <div className="mt-3 flex flex-wrap gap-1.5">
                                        <button
                                          type="button"
                                          onClick={() => setConfirmedBlockReviewStatus(block, "ready")}
                                          className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400"
                                        >
                                          Mark ready
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setConfirmedBlockReviewStatus(block, "blocked")}
                                          className="rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700 dark:bg-red-500 dark:text-red-950 dark:hover:bg-red-400"
                                        >
                                          Mark blocked
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setConfirmedBlockReviewStatus(block, "needs_review")}
                                          className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                        >
                                          Needs review
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => editConfirmedBlockReviewNote(block)}
                                          className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                        >
                                          Review note
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showInternalCommitments ? (
        <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-3xl flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <CalendarDays className="h-4 w-4" />
                  Internal commitments
                </div>
                <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100">Commitment review</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {internalCommitmentSummary.count} commitments · {internalCommitmentSummary.planned} planned · {internalCommitmentSummary.superseded} superseded · {internalCommitmentSummary.cancelled} cancelled
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={exportInternalCommitmentsCsv}
                  disabled={internalCommitmentSummary.count === 0}
                  className="rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                >
                  Export commitments CSV
                </button>
                {canManage ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowPublishReadiness((current) => !current)}
                      disabled={eligiblePublishCommitmentIds.size === 0}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Dry run publish
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReconciliation((current) => !current)}
                      disabled={eligibleReconcileCommitmentIds.size === 0}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Reconcile FPT
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => setShowInternalCommitments(false)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-5 py-4">
            {showPublishReadiness ? (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/70 dark:bg-blue-950/25">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Publish readiness</div>
                    <h3 className="mt-1 text-sm font-semibold text-blue-950 dark:text-blue-100">Dry-run validation only</h3>
                    <p className="mt-1 text-xs text-blue-800/80 dark:text-blue-200/80">
                      No FPT or Google Calendar records are created. Planned commitments are selected by default.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={selectAllPublishEligibleCommitments}
                      disabled={eligiblePublishCommitmentIds.size === 0}
                      className="rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-100 dark:hover:bg-blue-950/50"
                    >
                      Select planned
                    </button>
                    <button
                      type="button"
                      onClick={clearPublishCommitmentSelection}
                      disabled={selectedCommitmentIds.size === 0}
                      className="rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-100 dark:hover:bg-blue-950/50"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => runPublishDryRun(Array.from(selectedCommitmentIds))}
                      disabled={selectedCommitmentIds.size === 0 || dryRunLoading}
                      className="rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-100 dark:hover:bg-blue-950/50"
                    >
                      {dryRunLoading ? "Running..." : "Run preview"}
                    </button>
                    <button
                      type="button"
                      onClick={exportDryRunJson}
                      disabled={dryRunResults.length === 0 || dryRunLoading}
                      className="rounded-md bg-blue-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-400 dark:text-blue-950 dark:hover:bg-blue-300"
                    >
                      Export dry-run JSON
                    </button>
                    <button
                      type="button"
                      onClick={publishSelectedCommitmentsToFpt}
                      disabled={!dryRunReport?.dryRunToken || dryRunSummary.blockedCount > 0 || publishLoading}
                      className="rounded-md bg-red-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-500 dark:text-red-950 dark:hover:bg-red-400"
                    >
                      {publishLoading ? "Publishing..." : "Publish to FPT"}
                    </button>
                  </div>
                </div>
                {dryRunReport?.dryRunToken ? (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs text-blue-800 dark:border-blue-900/60 dark:bg-slate-950 dark:text-blue-100">
                    Dry-run token ready until {dryRunReport.expiresAt ? new Date(dryRunReport.expiresAt).toLocaleTimeString() : "soon"}. Publishing is enabled only for this exact selection.
                  </div>
                ) : null}
                {publishReport ? (
                  <div className={cn("mt-3 rounded-lg border px-3 py-2 text-xs", publishReport.summary.failed > 0 ? "border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100" : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100")}>
                    FPT publish result: {publishReport.summary.created} created · {publishReport.summary.updated} updated · {publishReport.summary.skipped} skipped · {publishReport.summary.failed} failed
                    {publishReport.results.some((result) => result.error || result.reason) ? (
                      <div className="mt-2 space-y-1">
                        {publishReport.results.filter((result) => result.error || result.reason).map((result) => (
                          <div key={`${result.commitmentId}-${result.action}`}>{result.commitmentId}: {result.error || result.reason}</div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="mt-3 grid gap-2 sm:grid-cols-4 lg:grid-cols-7">
                  <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{dryRunSummary.selectedCount}</span> selected</div>
                  <div className="rounded-lg bg-white px-3 py-2 text-xs text-emerald-700 dark:bg-slate-950 dark:text-emerald-300"><span className="font-semibold">{dryRunSummary.readyCount}</span> ready</div>
                  <div className="rounded-lg bg-white px-3 py-2 text-xs text-amber-700 dark:bg-slate-950 dark:text-amber-300"><span className="font-semibold">{dryRunSummary.warningCount}</span> warning</div>
                  <div className="rounded-lg bg-white px-3 py-2 text-xs text-red-700 dark:bg-slate-950 dark:text-red-300"><span className="font-semibold">{dryRunSummary.blockedCount}</span> blocked</div>
                  <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{dryRunSummary.createCount}</span> create</div>
                  <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{dryRunSummary.updateCount}</span> update</div>
                  <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{dryRunSummary.skipCount}</span> skip</div>
                </div>
                {dryRunResults.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {dryRunResults.map((result) => (
                      <div key={result.commitmentId} className="rounded-lg border border-blue-100 bg-white p-3 text-xs dark:border-blue-900/60 dark:bg-slate-950">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-semibold text-slate-950 dark:text-slate-100">{result.commitment?.projectName || result.commitment?.projectId || result.commitmentId} · {result.commitment?.personName || result.commitment?.personId || "Unknown artist"}</div>
                            <div className="mt-1 text-slate-500 dark:text-slate-400">
                              FPT: {result.fptAction} · Google Calendar: {result.googleCalendarAction}
                            </div>
                            <div className="mt-1 text-slate-500 dark:text-slate-400">
                              PlanningBlock {result.sourceReferences.sourcePlanningBlockId || "missing"}
                              {result.sourceReferences.sourceFptBookingId ? ` · FPT booking ${result.sourceReferences.sourceFptBookingId}` : ""}
                            </div>
                          </div>
                          <span className={cn(
                            "rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                            result.status === "ready" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
                            result.status === "warning" && "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100",
                            result.status === "blocked" && "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100"
                          )}>{result.status}</span>
                        </div>
                        {result.validationErrors.length > 0 ? (
                          <div className="mt-2 rounded bg-red-50 px-2 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-200">
                            Blocking: {result.validationErrors.join(" ")}
                          </div>
                        ) : null}
                        {result.validationWarnings.length > 0 ? (
                          <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                            Warnings: {result.validationWarnings.join(" ")}
                          </div>
                        ) : null}
                        {result.fptPayloadPreview || result.googleCalendarPayloadPreview ? (
                          <div className="mt-2 grid gap-2 md:grid-cols-2">
                            {result.fptPayloadPreview ? (
                              <pre className="max-h-40 overflow-auto rounded bg-slate-950 p-2 text-[11px] text-slate-100">{JSON.stringify(result.fptPayloadPreview, null, 2)}</pre>
                            ) : null}
                            {result.googleCalendarPayloadPreview ? (
                              <pre className="max-h-40 overflow-auto rounded bg-slate-950 p-2 text-[11px] text-slate-100">{JSON.stringify(result.googleCalendarPayloadPreview, null, 2)}</pre>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-blue-800/80 dark:bg-slate-950 dark:text-blue-200/80">
                    Select planned internal commitments below to see dry-run readiness results.
                  </div>
                )}
              </div>
            ) : null}
            {showReconciliation ? (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/70 dark:bg-amber-950/25">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">FPT reconciliation</div>
                    <h3 className="mt-1 text-sm font-semibold text-amber-950 dark:text-amber-100">Read-only comparison and controlled update</h3>
                    <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
                      Reads selected FPT Booking records and compares them with internal commitments. Updates require a fresh dry run and explicit confirmation; rollback remains manual guidance only.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={selectAllReconcileEligibleCommitments}
                      disabled={eligibleReconcileCommitmentIds.size === 0}
                      className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-800 dark:bg-slate-950 dark:text-amber-100 dark:hover:bg-amber-950/50"
                    >
                      Select FPT-linked
                    </button>
                    <button
                      type="button"
                      onClick={clearReconcileCommitmentSelection}
                      disabled={selectedReconcileCommitmentIds.size === 0}
                      className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-800 dark:bg-slate-950 dark:text-amber-100 dark:hover:bg-amber-950/50"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={reconcileSelectedCommitmentsWithFpt}
                      disabled={selectedReconcileCommitmentIds.size === 0 || reconcileLoading}
                      className="rounded-md bg-amber-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
                    >
                      {reconcileLoading ? "Reconciling..." : "Reconcile with FPT"}
                    </button>
                    <button
                      type="button"
                      onClick={runRepublishUpdateDryRun}
                      disabled={selectedReconcileCommitmentIds.size === 0 || reconcileLoading || republishLoading}
                      className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-800 dark:bg-slate-950 dark:text-amber-100 dark:hover:bg-amber-950/50"
                    >
                      Dry run update
                    </button>
                    <button
                      type="button"
                      onClick={republishSelectedCommitmentsToFpt}
                      disabled={!republishDryRunReport?.dryRunToken || (republishDryRunReport?.summary.blockedCount || 0) > 0 || republishLoading}
                      className="rounded-md bg-red-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-red-500 dark:text-red-950 dark:hover:bg-red-400"
                    >
                      {republishLoading ? "Updating..." : "Republish update"}
                    </button>
                  </div>
                </div>
                {republishDryRunReport?.dryRunToken ? (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-slate-950 dark:text-amber-100">
                    Update dry-run token ready until {republishDryRunReport.expiresAt ? new Date(republishDryRunReport.expiresAt).toLocaleTimeString() : "soon"}. Only existing FPT bookings will be updated.
                  </div>
                ) : null}
                {republishReport ? (
                  <div className={cn("mt-3 rounded-lg border px-3 py-2 text-xs", republishReport.summary.failed > 0 ? "border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100" : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100")}>
                    FPT update result: {republishReport.summary.updated} updated · {republishReport.summary.skipped} skipped · {republishReport.summary.failed} failed
                    {republishReport.results.some((result) => result.error || result.reason) ? (
                      <div className="mt-2 space-y-1">
                        {republishReport.results.filter((result) => result.error || result.reason).map((result) => (
                          <div key={`${result.commitmentId}-${result.action}`}>{result.commitmentId}: {result.error || result.reason}</div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {republishDryRunReport ? (
                  <div className="mt-3 grid gap-2 sm:grid-cols-4">
                    <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{republishDryRunReport.summary.selectedCount}</span> selected</div>
                    <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{republishDryRunReport.summary.updateCount}</span> update</div>
                    <div className="rounded-lg bg-white px-3 py-2 text-xs text-red-700 dark:bg-slate-950 dark:text-red-300"><span className="font-semibold">{republishDryRunReport.summary.blockedCount}</span> blocked</div>
                    <div className="rounded-lg bg-white px-3 py-2 text-xs text-amber-700 dark:bg-slate-950 dark:text-amber-300"><span className="font-semibold">{republishDryRunReport.summary.warningCount}</span> warning</div>
                  </div>
                ) : null}
                {reconcileReport ? (
                  <div className="mt-3 space-y-3">
                    <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
                      <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{reconcileReport.summary.matched}</span> matched</div>
                      <div className="rounded-lg bg-white px-3 py-2 text-xs text-amber-700 dark:bg-slate-950 dark:text-amber-300"><span className="font-semibold">{reconcileReport.summary.drifted}</span> drifted</div>
                      <div className="rounded-lg bg-white px-3 py-2 text-xs text-red-700 dark:bg-slate-950 dark:text-red-300"><span className="font-semibold">{reconcileReport.summary.missing}</span> missing</div>
                      <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{reconcileReport.summary.blocked}</span> blocked</div>
                      <div className="rounded-lg bg-white px-3 py-2 text-xs text-red-700 dark:bg-slate-950 dark:text-red-300"><span className="font-semibold">{reconcileReport.summary.failed}</span> failed</div>
                      <div className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-slate-950"><span className="font-semibold">{reconcileReport.summary.total}</span> total</div>
                    </div>
                    <div className="space-y-2">
                      {reconcileReport.results.map((result) => (
                        <div key={result.commitmentId} className="rounded-lg border border-amber-100 bg-white p-3 text-xs dark:border-amber-900/60 dark:bg-slate-950">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-slate-950 dark:text-slate-100">{result.commitmentId}</div>
                              <div className="mt-1 text-slate-500 dark:text-slate-400">FPT booking {result.fptBookingId || "unknown"}</div>
                            </div>
                            <span className={cn(
                              "rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                              result.status === "matched" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
                              result.status === "drifted" && "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100",
                              ["missing", "blocked", "failed"].includes(result.status) && "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100"
                            )}>{result.status}</span>
                          </div>
                          {result.differences && result.differences.length > 0 ? (
                            <div className="mt-2 space-y-1">
                              {result.differences.map((difference) => (
                                <div key={`${result.commitmentId}-${difference.field}`} className="rounded bg-amber-50 px-2 py-1 text-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                                  {difference.field}: local {String(difference.local ?? "")} / FPT {String(difference.fpt ?? "")}
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {result.errors && result.errors.length > 0 ? (
                            <div className="mt-2 rounded bg-red-50 px-2 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-200">{result.errors.join(" ")}</div>
                          ) : null}
                          {result.rollbackGuidance && result.rollbackGuidance.length > 0 ? (
                            <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                              Guidance: {result.rollbackGuidance.join(" ")}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-amber-800/80 dark:bg-slate-950 dark:text-amber-200/80">
                    Select published or FPT-linked commitments below, then run reconciliation.
                  </div>
                )}
              </div>
            ) : null}
            <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Publish history</div>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Recent dry-runs and FPT publish attempts. Payload previews are kept backend-side without credentials.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={loadData}
                  className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                >
                  Refresh history
                </button>
              </div>
              {publishAuditRecords.length === 0 ? (
                <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  No publish audit records yet.
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  {publishAuditRecords.slice(0, 8).map((record) => {
                    const expanded = expandedPublishAuditId === record.id;
                    const created = record.createdAt ? new Date(record.createdAt).toLocaleString() : "Unknown time";
                    const createdCount = getAuditCount(record.summary, ["created", "createCount"]);
                    const updatedCount = getAuditCount(record.summary, ["updated", "updateCount"]);
                    const skippedCount = getAuditCount(record.summary, ["skipped", "skipCount"]);
                    const failedCount = getAuditCount(record.summary, ["failed", "blockedCount"]);
                    return (
                      <div key={record.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-900">
                        <button
                          type="button"
                          onClick={() => setExpandedPublishAuditId(expanded ? null : record.id)}
                          className="flex w-full flex-wrap items-start justify-between gap-3 text-left"
                        >
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-slate-100">{created}</div>
                            <div className="mt-1 text-slate-500 dark:text-slate-400">
                              {record.createdBy || "unknown user"} · {record.action.replace("_", " ")} · {record.target.replace("_", " ")}
                            </div>
                            <div className="mt-1 text-slate-500 dark:text-slate-400">
                              {record.commitmentIds.length} commitment{record.commitmentIds.length === 1 ? "" : "s"} · {createdCount} created · {updatedCount} updated · {skippedCount} skipped · {failedCount} failed
                            </div>
                          </div>
                          <span className={cn(
                            "rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                            record.status === "success" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
                            record.status === "partial_success" && "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100",
                            record.status === "failed" && "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100"
                          )}>{record.status.replace("_", " ")}</span>
                        </button>
                        {expanded ? (
                          <div className="mt-3 space-y-2">
                            {record.expiresAt ? (
                              <div className="rounded bg-white px-2 py-1 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                                Dry-run token expired/expires at {new Date(record.expiresAt).toLocaleString()}
                              </div>
                            ) : null}
                            {record.errorMessage ? (
                              <div className="rounded bg-red-50 px-2 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-200">{record.errorMessage}</div>
                            ) : null}
                            {(record.results || []).slice(0, 12).map((result, index) => (
                              <div key={`${record.id}-${index}`} className="rounded bg-white px-2 py-1 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                                {String(result.commitmentId || "unknown commitment")} · {String(result.action || result.fptAction || result.status || "result")}
                                {result.fptBookingId ? ` · FPT ${String(result.fptBookingId)}` : ""}
                                {result.reason || result.error ? ` · ${String(result.reason || result.error)}` : ""}
                              </div>
                            ))}
                            {(record.results || []).length > 12 ? (
                              <div className="text-slate-500 dark:text-slate-400">Showing 12 of {(record.results || []).length} result rows.</div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {visibleInternalCommitments.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                No internal booking commitments match the current date, project, and department filters.
              </div>
            ) : (
              <div className="space-y-5">
                {internalCommitmentGroups.map((projectGroup) => (
                  <div key={projectGroup.projectName} className="space-y-3">
                    <div className="sticky top-0 z-10 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                      {projectGroup.projectName}
                    </div>
                    {projectGroup.departmentGroups.map((departmentGroup) => (
                      <div key={`${projectGroup.projectName}-${departmentGroup.department}`} className="space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{departmentGroup.department}</div>
                        {departmentGroup.artistGroups.map((artistGroup) => (
                          <div key={`${projectGroup.projectName}-${departmentGroup.department}-${artistGroup.artistName}`} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                            <div className="text-sm font-semibold text-slate-950 dark:text-slate-100">{artistGroup.artistName}</div>
                            <div className="mt-2 space-y-2">
                              {artistGroup.commitments.map((commitment) => (
                                <div key={commitment.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-900">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex min-w-0 gap-2">
                                      {showPublishReadiness && canManage ? (
                                        <input
                                          type="checkbox"
                                          checked={selectedCommitmentIds.has(commitment.id)}
                                          onChange={() => togglePublishCommitmentSelection(commitment.id)}
                                          disabled={!eligiblePublishCommitmentIds.has(commitment.id)}
                                          aria-label={`Select ${commitment.projectName || commitment.id} for publish dry run`}
                                          className="mt-0.5 h-4 w-4 rounded border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                        />
                                      ) : null}
                                      {showReconciliation && canManage ? (
                                        <input
                                          type="checkbox"
                                          checked={selectedReconcileCommitmentIds.has(commitment.id)}
                                          onChange={() => toggleReconcileCommitmentSelection(commitment.id)}
                                          disabled={!eligibleReconcileCommitmentIds.has(commitment.id)}
                                          aria-label={`Select ${commitment.projectName || commitment.id} for FPT reconciliation`}
                                          className="mt-0.5 h-4 w-4 rounded border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                                        />
                                      ) : null}
                                      <div className="min-w-0">
                                      <div className="font-semibold text-slate-900 dark:text-slate-100">{commitment.projectName || commitment.projectId || "No project"}</div>
                                      <div className="mt-1 text-slate-500 dark:text-slate-400">
                                        {commitment.startDate} to {commitment.endDate} · {commitment.allocationPercent}% · {commitment.blockType}
                                      </div>
                                      <div className="mt-1 text-slate-500 dark:text-slate-400">
                                        PlanningBlock {commitment.sourcePlanningBlockId}
                                        {commitment.sourceFptBookingId ? ` · FPT ${commitment.sourceFptBookingId}` : ""}
                                      </div>
                                      <div className="mt-1 text-slate-500 dark:text-slate-400">
                                        Created {new Date(commitment.createdAt).toLocaleString()} · Updated {new Date(commitment.updatedAt).toLocaleString()}
                                      </div>
                                      <div className="mt-1 text-slate-500 dark:text-slate-400">
                                        Committed by {commitment.committedBy || commitment.createdBy || "unknown"} at {commitment.committedAt ? new Date(commitment.committedAt).toLocaleString() : "unknown"}
                                      </div>
                                      {commitment.lastConvertedAt ? (
                                        <div className="mt-1 text-slate-500 dark:text-slate-400">
                                          Last converted {new Date(commitment.lastConvertedAt).toLocaleString()} · Run {commitment.conversionRunId || "unknown"}
                                        </div>
                                      ) : null}
                                      </div>
                                    </div>
                                    <div className="flex shrink-0 flex-col items-end gap-1">
                                      <span className={cn(
                                        "rounded-full px-2 py-0.5 text-[11px] font-medium capitalize",
                                        commitment.commitmentStatus === "planned" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
                                        commitment.commitmentStatus === "superseded" && "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100",
                                        commitment.commitmentStatus === "cancelled" && "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100"
                                      )}>{commitment.commitmentStatus}</span>
                                      <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white dark:bg-slate-100 dark:text-slate-950">Internal</span>
                                      {commitment.publishStatus === "published" ? (
                                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 dark:bg-blue-900/60 dark:text-blue-100">Published to FPT</span>
                                      ) : null}
                                      {commitment.publishStatus === "failed" ? (
                                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800 dark:bg-red-900/60 dark:text-red-100">Publish failed</span>
                                      ) : null}
                                    </div>
                                  </div>
                                  {commitment.externalBookingId || commitment.publishedToFptAt ? (
                                    <div className="mt-2 rounded bg-white px-2 py-1 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                                      FPT booking {commitment.externalBookingId || "unknown"} · published {commitment.publishedToFptAt ? new Date(commitment.publishedToFptAt).toLocaleString() : "pending"} by {commitment.publishedBy || "unknown"}
                                    </div>
                                  ) : null}
                                  {commitment.lastPublishError ? (
                                    <div className="mt-2 rounded bg-red-50 px-2 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-200">
                                      Last publish error: {commitment.lastPublishError}
                                    </div>
                                  ) : null}
                                  {commitment.note ? <div className="mt-2 rounded bg-white px-2 py-1 text-slate-600 dark:bg-slate-950 dark:text-slate-300">{commitment.note}</div> : null}
                                  {canManage ? (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                      {(["planned", "superseded", "cancelled"] as InternalBookingCommitmentStatus[]).map((status) => (
                                        <button
                                          key={status}
                                          type="button"
                                          onClick={() => updateInternalCommitmentStatus(commitment, status)}
                                          disabled={commitment.commitmentStatus === status}
                                          className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium capitalize text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                        >
                                          {status}
                                        </button>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {linkingBooking ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-sm font-semibold text-slate-950 dark:text-slate-100">{linkingBooking.resolutionSource === "manual" ? "Relink FPT booking" : "Link FPT booking to artist"}</h2>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">This only updates local person resolution metadata. FPT is not changed.</p>
              </div>
              <button onClick={closeLinkBookingModal} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="font-semibold text-slate-900 dark:text-slate-100">{getBookingLabel(linkingBooking)}</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">FPT user/person: {getUnmappedBookingPersonLabel(linkingBooking)}</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Resolution: {getBookingResolutionLabel(linkingBooking)}</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {linkingBooking.startDate || "No start"} to {linkingBooking.endDate || "No end"} · {getBookingAllocation(linkingBooking)}% · {linkingBooking.bookingStatus || "No status"}
                </div>
                {linkingBooking.note ? <div className="mt-2 rounded bg-white px-2 py-1 text-xs text-slate-600 dark:bg-slate-950 dark:text-slate-300">{linkingBooking.note}</div> : null}
              </div>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Local artist</span>
                <select
                  value={linkingPersonId}
                  onChange={(event) => setLinkingPersonId(event.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                >
                  <option value="">Choose artist</option>
                  {artists.map((artist) => (
                    <option key={artist.id} value={artist.id}>{artist.name} · {artist.department || "Unassigned"}</option>
                  ))}
                </select>
              </label>

              {linkingError ? (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-200">
                  <AlertTriangle className="h-4 w-4" />
                  {linkingError}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/60">
              {linkingBooking.resolutionSource === "manual" ? (
                <button onClick={unlinkBookingFromArtist} disabled={linkingSaving} className="mr-auto rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 dark:border-red-900/60 dark:bg-slate-950 dark:hover:bg-red-950/20">
                  Undo manual link
                </button>
              ) : null}
              <button onClick={closeLinkBookingModal} disabled={linkingSaving} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900">Cancel</button>
              <button onClick={linkBookingToArtist} disabled={linkingSaving || !linkingPersonId} className="rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-950">
                {linkingSaving ? "Saving..." : linkingBooking.resolutionSource === "manual" ? "Save relink" : "Link booking"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PlanningBlockModal
        isOpen={modalOpen}
        block={editingBlock}
        artists={artists}
        projects={projects}
        defaultStartDate={rangeStart}
        defaultEndDate={rangeStart}
        onClose={() => {
          setModalOpen(false);
          setDraftBlockDefaults(null);
        }}
        draftDefaults={draftBlockDefaults}
        onSave={saveBlock}
        onDelete={deleteBlock}
        canDelete={canManage}
      />
    </div>
  );
}
